{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2025 - 2026                               }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit TMS.TMSFNCFilterBuilder;

interface

uses
  Classes, TMS.TMSFNCValue, Generics.Collections, SysUtils, Rtti;

resourcestring
  sTMSFNCFilterBuilderUILanguageClose = 'Close';
  sTMSFNCFilterBuilderUILanguageApply = 'Apply';
  sTMSFNCFilterBuilderUILanguageOk = 'OK';
  sTMSFNCFilterBuilderUILanguageClear = 'Clear';
  sTMSFNCFilterBuilderUILanguageCancel = 'Cancel';
  sTMSFNCFilterBuilderUILanguageCaption = 'Filter';
  sTMSFNCFilterBuilderUILanguageAndOperatorText = 'AND';
  sTMSFNCFilterBuilderUILanguageOrOperatorText = 'OR';
  sTMSFNCFilterBuilderUILanguageEmptyOperatorText = 'is Empty';
  sTMSFNCFilterBuilderUILanguageEqualOperatorText = 'equals';
  sTMSFNCFilterBuilderUILanguageEqualOperatorNumber = '=';
  sTMSFNCFilterBuilderUILanguageNotEqualOperatorNumber = '<>';
  sTMSFNCFilterBuilderUILanguageLargerThanOrEqualOperatorText = '>=';
  sTMSFNCFilterBuilderUILanguageLargerThanOperatorText = '>';
  sTMSFNCFilterBuilderUILanguageSmallerThanOrEqualOperatorText = '<=';
  sTMSFNCFilterBuilderUILanguageSmallerThanOperatorText = '<';
  sTMSFNCFilterBuilderUILanguageNotEmptyOperatorText = 'is Not Empty';
  sTMSFNCFilterBuilderUILanguageNotEqualOperatorText = 'is Not Equal';
  sTMSFNCFilterBuilderUILanguageStartsWithOperatorText = 'starts with';
  sTMSFNCFilterBuilderUILanguageEndsWithOperatorText = 'ends with';
  sTMSFNCFilterBuilderUILanguageContainsOperatorText = 'contains';
  sTMSFNCFilterBuilderUILanguageNotContainsOperatorText = 'does Not Contain';
  sTMSFNCFilterBuilderUILanguageAddExpression = 'Add Expression';
  sTMSFNCFilterBuilderUILanguageAddGroup = 'Add Group';
  sTMSFNCFilterBuilderUILanguageAddHint = 'Add Condition';
  sTMSFNCFilterBuilderUILanguageRemoveHint = 'Remove Condition';
  sTMSFNCFilterBuilderUILanguageNotHint = 'Invert Expression';
  sTMSFNCFilterBuilderUILanguageColumnNameHint = 'Set Column Name';
  sTMSFNCFilterBuilderUILanguageOperatorHint = 'Set Operator';
  sTMSFNCFilterBuilderUILanguageValueHint = 'Set Value';
//  sTMSFNCFilterBuilderUILanguageCaseHint = 'Set Case Sensitivity';

  sTMSFNCFilterBuilderUILanguageTrue = 'True';
  sTMSFNCFilterBuilderUILanguageFalse = 'False';

const
  MAJ_VER = 1; // Major version nr.
  MIN_VER = 0; // Minor version nr.
  REL_VER = 0; // Release nr.
  BLD_VER = 4; // Build nr.

  //version history
  //v0.9.0.0: First BETA Release
  //v0.9.1.0: New : DisplayNames added in columns to use more readable names without affecting the filter
  //        : Improved : fdtOther introduced for non-standard types in FilterDialog
  //v1.0.0.0: First release
  //v1.0.0.1: New : ValidateFilterArray can be used for data per row or data per column.
  //        : Fixed : Issue event called
  //v1.0.0.2: Improved : Expression operation parsing order
  //v1.0.0.3: Improved : Problems with empty values in WEB Core
  //v1.0.0.4: Improved : DateTime parsing in older Delphi versions

type
  TTMSFNCFilterBuilderDataType = (fdtAutomatic, fdtText, fdtBoolean, fdtDateTime, fdtDate, fdtTime, fdtFloat, fdtNumber, fdtOther);

  TTMSFNCFilterBuilderGroupOperator = (fgoAND, fgoOR{, fgoXOR});
  TTMSFNCFilterBuilderExpressionOperator = (feoStartsWith, feoEndsWith, feoContains, feoEqual, feoNotEqual, feoEmpty, feoNotEmpty, feoLargerThan,
    feoSmallerThan, feoLargerThanOrEqual, feoSmallerThanOrEqual);

const
  DefaultTextOperators = [feoEqual, feoNotEqual, feoNotEmpty, feoEmpty, feoContains, feoStartsWith, feoEndsWith];
  DefaultNumberOperators = [feoNotEmpty, feoEmpty, feoEqual, feoNotEqual, feoLargerThan, feoLargerThanOrEqual, feoSmallerThan, feoSmallerThanOrEqual];
  {$IFDEF WEBLIB}
  INFINITE = Cardinal($FFFFFFFF);
  {$ENDIF}
type
  TTMSFNCFilterBuilderCustomUILanguage = class(TPersistent)
  private
    FCaption: string;
    FClose: string;
    FAndOperatorText: string;
    FOrOperatorText: string;
    FOnInternalChanged: TNotifyEvent;
    FSmallerThanOrEqualOperatorText: string;
    FLargerThanOperatorText: string;
    FEqualOperatorText: string;
    FSmallerThanOperatorText: string;
    FNotEqualOperatorText: string;
    FEmptyOperatorText: string;
    FLargerThanOrEqualOperatorText: string;
    FNotEmptyOperatorText: string;
    FStartsWithOperatorText: string;
    FEndsWithOperatorText: string;
    FContainsOperatorText: string;
    FNotContainsOperatorText: string;
    FCancel: string;
    FApply: string;
    FClear: string;
    FAddGroup: string;
    FAddExpression: string;
    FFalseText: string;
    FTrueText: string;
    FOperatorHint: string;
    FNotHint: string;
    FAddHint: string;
    FValueHint: string;
    FRemoveHint: string;
    FColumnNameHint: string;
    FOK: string;
//    FCaseHint: string;
    function IsCaptionStored: Boolean;
    function IsCloseStored: Boolean;
    function IsAndOperatorTextStored: Boolean;
    function IsOrOperatorTextStored: Boolean;
    function IsEmptyOperatorTextStored: Boolean;
    function TrueOperatorTextStored: Boolean;
    function FalseOperatorTextStored: Boolean;
    function IsEqualOperatorTextStored: Boolean;
    function IsLargerThanOrEqualOperatorTextStored: Boolean;
    function IsLargerThanOperatorTextStored: Boolean;
    function IsSmallerThanOrEqualOperatorTextStored: Boolean;
    function IsSmallerThanOperatorTextStored: Boolean;
    function IsNotEmptyOperatorTextStored: Boolean;
    function IsNotEqualOperatorTextStored: Boolean;
    function IsContainsOperatorTextStored: Boolean;
    function IsEndsWithOperatorTextStored: Boolean;
    function IsNotContainsOperatorTextStored: Boolean;
    function IsStartsWithOperatorTextStored: Boolean;
    function IsApplyStored: Boolean;
    function IsCancelStored: Boolean;
    function IsClearStored: Boolean;
    function IsAddExpressionStored: Boolean;
    function IsAddGroupStored: Boolean;
    function IsAddHintStored: Boolean;
    function IsColumnNameHintStored: Boolean;
    function IsNotHintStored: Boolean;
    function IsOperatorHintStored: Boolean;
    function IsRemoveHintStored: Boolean;
    function IsValueHintStored: Boolean;
    function IsOKStored: Boolean;
//    function IsCaseHintStored: Boolean;
  protected
    procedure DoChanged;
    property Clear: string read FClear write FClear stored IsClearStored;
    property Apply: string read FApply write FApply stored IsApplyStored;
    property OK: string read FOK write FOK stored IsOKStored;
    property Cancel: string read FCancel write FCancel stored IsCancelStored;
    property Close: string read FClose write FClose stored IsCloseStored;
    property Caption: string read FCaption write FCaption stored IsCaptionStored;
    property AndOperatorText: string read FAndOperatorText write FAndOperatorText stored IsAndOperatorTextStored;
    property OrOperatorText: string read FOrOperatorText write FOrOperatorText stored IsOrOperatorTextStored;
    property EqualOperatorText: string read FEqualOperatorText write FEqualOperatorText stored IsEqualOperatorTextStored;
    property NotEqualOperatorText: string read FNotEqualOperatorText write FNotEqualOperatorText stored IsNotEqualOperatorTextStored;
    property SmallerThanOperatorText: string read FSmallerThanOperatorText write FSmallerThanOperatorText stored IsSmallerThanOperatorTextStored;
    property SmallerThanOrEqualOperatorText: string read FSmallerThanOrEqualOperatorText write FSmallerThanOrEqualOperatorText stored IsSmallerThanOrEqualOperatorTextStored;
    property LargerThanOperatorText: string read FLargerThanOperatorText write FLargerThanOperatorText stored IsLargerThanOperatorTextStored;
    property LargerThanOrEqualOperatorText: string read FLargerThanOrEqualOperatorText write FLargerThanOrEqualOperatorText stored IsLargerThanOrEqualOperatorTextStored;
    property EmptyOperatorText: string read FEmptyOperatorText write FEmptyOperatorText stored IsEmptyOperatorTextStored;
    property NotEmptyOperatorText: string read FNotEmptyOperatorText write FNotEmptyOperatorText stored IsNotEmptyOperatorTextStored;
    property StartsWithOperatorText: string read FStartsWithOperatorText write FStartsWithOperatorText stored IsStartsWithOperatorTextStored;
    property EndsWithOperatorText: string read FEndsWithOperatorText write FEndsWithOperatorText stored IsEndsWithOperatorTextStored;
    property ContainsOperatorText: string read FContainsOperatorText write FContainsOperatorText stored IsContainsOperatorTextStored;
    property NotContainsOperatorText: string read FNotContainsOperatorText write FNotContainsOperatorText stored IsNotContainsOperatorTextStored;
    property TrueText: string read FTrueText write FTrueText stored TrueOperatorTextStored;
    property FalseText: string read FFalseText write FFalseText stored FalseOperatorTextStored;
    property AddExpression: string read FAddExpression write FAddExpression stored IsAddExpressionStored;
    property AddGroup: string read FAddGroup write FAddGroup stored IsAddGroupStored;
    property AddHint: string read FAddHint write FAddHint stored IsAddHintStored;
    property RemoveHint: string read FRemoveHint write FRemoveHint stored IsRemoveHintStored;
    property NotHint: string read FNotHint write FNotHint stored IsNotHintStored;
//    property CaseHint: string read FCaseHint write FCaseHint stored IsCaseHintStored;
    property ColumnNameHint: string read FColumnNameHint write FColumnNameHint stored IsColumnNameHintStored;
    property OperatorHint: string read FOperatorHint write FOperatorHint stored IsOperatorHintStored;
    property ValueHint: string read FValueHint write FValueHint stored IsValueHintStored;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
    property OnInternalChanged: TNotifyEvent read FOnInternalChanged write FOnInternalChanged;
  end;

  TTMSFNCFilterBuilderParseFormat = class(TPersistent)
  private
    FUpdateCount: Integer;
    FModified: Boolean;
    FAndOperator: string;
    FOrOperator: string;
    FSmallerThanOrEqualOperator: string;
    FLargerThanOperator: string;
    FEqualOperator: string;
    FSmallerThanOperator: string;
    FNotEqualOperator: string;
    FEmptyOperator: string;
    FLargerThanOrEqualOperator: string;
    FNotEmptyOperator: string;
    FStartsWithOperator: string;
    FEndsWithOperator: string;
    FContainsOperator: string;
    FColumnFormat: string;
    FGroupFormat: string;
    FOnInternalChanged: TNotifyEvent;
//    FXOrOperator: string;
    FSingleCharWildCard: string;
    FMultiCharWildCard: string;
    FStringDelimiter: string;
    FNotOperator: string;
    FEqualStrOperator: string;
    function IsAndOperatorStored: Boolean;
    function IsOrOperatorStored: Boolean;
    function IsEmptyOperatorStored: Boolean;
    function IsEqualOperatorStored: Boolean;
    function IsLargerThanOrEqualOperatorStored: Boolean;
    function IsLargerThanOperatorStored: Boolean;
    function IsSmallerThanOrEqualOperatorStored: Boolean;
    function IsSmallerThanOperatorStored: Boolean;
    function IsNotEmptyOperatorStored: Boolean;
    function IsNotEqualOperatorStored: Boolean;
    function IsContainsOperatorStored: Boolean;
    function IsEndsWithOperatorStored: Boolean;
    function IsStartsWithOperatorStored: Boolean;
    function IsColumnFormatStored: Boolean;
    function IsGroupFormatStored: Boolean;
//    function IsXOrOperatorStored: Boolean;
    function IsMultiCharWildCardStored: Boolean;
    function IsSingleCharWildCardStored: Boolean;
    function IsStringDelimiterStored: Boolean;
    procedure SetAndOperator(const Value: string);
    procedure SetColumnFormat(const Value: string);
    procedure SetContainsOperator(const Value: string);
    procedure SetEmptyOperator(const Value: string);
    procedure SetEndsWithOperator(const Value: string);
    procedure SetEqualOperator(const Value: string);
    procedure SetGroupFormat(const Value: string);
    procedure SetLargerThanOperator(const Value: string);
    procedure SetLargerThanOrEqualOperator(const Value: string);
    procedure SetMultiCharWildCard(const Value: string);
//    procedure SetNotContainsOperator(const Value: string);
    procedure SetNotEmptyOperator(const Value: string);
    procedure SetNotEqualOperator(const Value: string);
    procedure SetOrOperator(const Value: string);
    procedure SetSingleCharWildCard(const Value: string);
    procedure SetSmallerThanOperator(const Value: string);
    procedure SetSmallerThanOrEqualOperator(const Value: string);
    procedure SetStartsWithOperator(const Value: string);
    procedure SetStringDelimiter(const Value: string);
    function IsNotOperatorStored: Boolean;
    procedure SetNotOperator(const Value: string);
    function IsEqualStrOperatorStored: Boolean;
    procedure SetEqualStrOperator(const Value: string);
//    procedure SetXOrOperator(const Value: string);
  protected
    procedure DoInternalChanged;
    property OnInternalChanged: TNotifyEvent read FOnInternalChanged write FOnInternalChanged;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
    procedure BeginUpdate;
    procedure EndUpdate;
  published
    property AndOperator: string read FAndOperator write SetAndOperator stored IsAndOperatorStored;
    property OrOperator: string read FOrOperator write SetOrOperator stored IsOrOperatorStored;
//    property XOrOperator: string read FXOrOperator write SetXOrOperator stored IsXOrOperatorStored;
    property NotOperator: string read FNotOperator write SetNotOperator stored IsNotOperatorStored;
    property EqualOperator: string read FEqualOperator write SetEqualOperator stored IsEqualOperatorStored;
    property EqualStrOperator: string read FEqualStrOperator write SetEqualStrOperator stored IsEqualStrOperatorStored;
    property NotEqualOperator: string read FNotEqualOperator write SetNotEqualOperator stored IsNotEqualOperatorStored;
    property SmallerThanOperator: string read FSmallerThanOperator write SetSmallerThanOperator stored IsSmallerThanOperatorStored;
    property SmallerThanOrEqualOperator: string read FSmallerThanOrEqualOperator write SetSmallerThanOrEqualOperator stored IsSmallerThanOrEqualOperatorStored;
    property LargerThanOperator: string read FLargerThanOperator write SetLargerThanOperator stored IsLargerThanOperatorStored;
    property LargerThanOrEqualOperator: string read FLargerThanOrEqualOperator write SetLargerThanOrEqualOperator stored IsLargerThanOrEqualOperatorStored;
    property EmptyOperator: string read FEmptyOperator write SetEmptyOperator stored IsEmptyOperatorStored;
    property NotEmptyOperator: string read FNotEmptyOperator write SetNotEmptyOperator stored IsNotEmptyOperatorStored;
    property StartsWithOperator: string read FStartsWithOperator write SetStartsWithOperator stored IsStartsWithOperatorStored;
    property EndsWithOperator: string read FEndsWithOperator write SetEndsWithOperator stored IsEndsWithOperatorStored;
    property ContainsOperator: string read FContainsOperator write SetContainsOperator stored IsContainsOperatorStored;
//    property NotContainsOperator: string read FNotContainsOperator write SetNotContainsOperator stored IsNotContainsOperatorStored;
    property ColumnFormat: string read FColumnFormat write SetColumnFormat stored IsColumnFormatStored;
    property GroupFormat: string read FGroupFormat write SetGroupFormat stored IsGroupFormatStored;
    property StringDelimiter: string read FStringDelimiter write SetStringDelimiter stored IsStringDelimiterStored;
    property MultiCharWildCard: string read FMultiCharWildCard write SetMultiCharWildCard stored IsMultiCharWildCardStored;
    property SingleCharWildCard: string read FSingleCharWildCard write SetSingleCharWildCard stored IsSingleCharWildCardStored;
  end;

  ITMSFNCFilterUILanguage = interface
  ['{4329CE04-4BBA-485C-9502-90D3CF597020}']
    function GetFilterUILanguage: TTMSFNCFilterBuilderCustomUILanguage;
  end;

  TTMSFNCFilterBuilderColumns = class;

  TTMSFNCFilterBuilderColumnProperties =  record
    Name: string;
    DisplayName: string;
    DataType: TTMSFNCFilterBuilderDataType;
  end;

  TTMSFNCFilterBuilderColumn = class(TPersistent)
  private
    FColumns: TTMSFNCFilterBuilderColumns;
    FName: string;
    FDisplayName: string;
    FDataType: TTMSFNCFilterBuilderDataType;
    FOnInternalChanged: TNotifyEvent;
    FDataPointer: Pointer;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
    procedure SetDataType(const Value: TTMSFNCFilterBuilderDataType);
    procedure SetName(const Value: string);
    function GetDisplayName: string;
    procedure SetDisplayName(const Value: string);
  protected
    procedure DoChanged;
    procedure DoListChanged(Sender: TObject);
    procedure DoRemoveColumn;
    procedure DoColumnNameChange;
    function GetIndex: Integer;
    property OnInternalChanged: TNotifyEvent read FOnInternalChanged write FOnInternalChanged;
  public
    constructor Create(AColumns: TTMSFNCFilterBuilderColumns);
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataString: String read FDataString write FDataString;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
  published
    property Name: string read FName write SetName;
    property DisplayName: string read GetDisplayName write SetDisplayName;
    property DataType: TTMSFNCFilterBuilderDataType read FDataType write SetDataType default fdtAutomatic;
  end;

  TTMSFNCFilterBuilderColumnClass = class of TTMSFNCFilterBuilderColumn;

  TTMSFNCFilterBuilderColumnEvent = procedure(Sender: TObject; AColumn: TTMSFNCFilterBuilderColumn) of object;

  TTMSFNCFilterBuilderColumns = class(TObjectList<TTMSFNCFilterBuilderColumn>)
  private
    FOnInternalChanged: TNotifyEvent;
    FOnInternalColumnRemove: TTMSFNCFilterBuilderColumnEvent;
    FOnInternalColumnNameChange: TTMSFNCFilterBuilderColumnEvent;
  protected
    procedure DoColumnRemove(AColumn: TTMSFNCFilterBuilderColumn);
    procedure DoColumnNameChange(AColumn: TTMSFNCFilterBuilderColumn);
    procedure DoChanged;
    property OnInternalChanged: TNotifyEvent read FOnInternalChanged write FOnInternalChanged;
    property OnInternalColumnRemove: TTMSFNCFilterBuilderColumnEvent read FOnInternalColumnRemove write FOnInternalColumnRemove;
    property OnInternalColumnNameChange: TTMSFNCFilterBuilderColumnEvent read FOnInternalColumnNameChange write FOnInternalColumnNameChange;
  public
    constructor Create(AOwner: TPersistent);
    procedure Assign(Source: TTMSFNCFilterBuilderColumns);
    function GetColumnIndex(AColumn: TTMSFNCFilterBuilderColumn): Integer;
    function Add(AColumnClass: TTMSFNCFilterBuilderColumnClass = nil): TTMSFNCFilterBuilderColumn; reintroduce;
    function AddColumn(AName: string; ADataType: TTMSFNCFilterBuilderDataType = fdtAutomatic; ADisplayName: string = ''): TTMSFNCFilterBuilderColumn;
  end;

  TTMSFNCFilterValidateInputArray = TArray<TArray<TTMSFNCValue>>;
  TTMSFNCFilterValidateInputRow = TArray<TTMSFNCValue>;
  TTMSFNCFilterValidateOutputArray = TArray<Boolean>;
  TTMSFNCFilterBuilderFormatType = (fftDelphiDataSet, fftUniversalFilterExpressions, fftCustom);

  TTMSFNCCustomFilterBuilder = class;

  TTMSFNCFilterBuilderGroup = class;
  TTMSFNCFilterBuilderExpressions = class;

  TTMSFNCGetFilterBuilderEvent = procedure(Sender: TObject; var AFilterBuilder: TTMSFNCCustomFilterBuilder) of object;

  TTMSFNCFilterBuilderExpression = class(TPersistent)
  private
    FBackupColName: string;
    FExpressionOperator: TTMSFNCFilterBuilderExpressionOperator;
    FValidExpression: boolean;
    FValue: TTMSFNCValue;
    FValueText: string;
    FColumn: TTMSFNCFilterBuilderColumn;
    FDataPointer: Pointer;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
    FFilterExpressions: TTMSFNCFilterBuilderExpressions;
    FIsInverted: Boolean;
    FCaseSensitive: Boolean;
    FOnGetFilterBuilder: TTMSFNCGetFilterBuilderEvent;
    procedure SetExpressionOperator(const AValue: TTMSFNCFilterBuilderExpressionOperator);
    procedure SetColumn(const AValue: TTMSFNCFilterBuilderColumn);
    function GetInternalExpression(ADisplayNames: Boolean = False): string;
    function GetExpression: string;
    procedure UpdateColumn;
    function GetParentGroup: TTMSFNCFilterBuilderGroup;
    function GetIndex: Integer;
    procedure SetValue(const AValue: TTMSFNCValue);
    procedure SetInverted(const AValue: Boolean);
    procedure SetCaseSensitive(const Value: Boolean);
  protected
    function InternalValidateExpression(AInput: TTMSFNCValue; ADataType: TTMSFNCFilterBuilderDataType = fdtAutomatic): Boolean;
    procedure DoChanged;
    procedure CheckExpression;
    procedure GenerateExpression(AExpression: string; ADataSet: Boolean = False);
    procedure RemoveExpression;
    property ValidExpression: boolean read FValidExpression;
  public
    function GetBuilder: TTMSFNCCustomFilterBuilder;
    procedure DoGetFilterBuilder(var AFilterBuilder: TTMSFNCCustomFilterBuilder);
    constructor Create(AFilterExpressions: TTMSFNCFilterBuilderExpressions);
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    procedure SetColumnByName(AName: string);
    function ValidateExpression(AInput: TTMSFNCValue): Boolean;
    function GetColumnName: string;
    function GetColumnDisplayName: string;
    function GetValueDataType: TTMSFNCFilterBuilderDataType;
    property ParentGroup: TTMSFNCFilterBuilderGroup read GetParentGroup;
    property Expression: string read GetExpression ;// write SetExpression;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataString: String read FDataString write FDataString;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
  published
    property Column: TTMSFNCFilterBuilderColumn read FColumn write SetColumn;
    property ExpressionOperator: TTMSFNCFilterBuilderExpressionOperator read FExpressionOperator write SetExpressionOperator default feoNotEmpty;
    property Value: TTMSFNCValue read FValue write SetValue;
    property IsInverted: Boolean read FIsInverted write SetInverted;
    property CaseSensitive: Boolean read FCaseSensitive write SetCaseSensitive default False;
    property OnGetFilterBuilder: TTMSFNCGetFilterBuilderEvent read FOnGetFilterBuilder write FOnGetFilterBuilder;
  end;

  TTMSFNCFilterBuilderExpressions = class(TObjectList<TTMSFNCFilterBuilderExpression>)
  private
    FParentGroup: TTMSFNCFilterBuilderGroup;
    FOnInternalChanged: TNotifyEvent;
    function GetBuilder: TTMSFNCCustomFilterBuilder;
  protected
    procedure DoChanged;
    function Add(AExpression: string): TTMSFNCFilterBuilderExpression; overload;
    function Add(AExpression: TTMSFNCFilterBuilderExpression): TTMSFNCFilterBuilderExpression; overload;
    procedure DoExpressionAdded(AExpression: TTMSFNCFilterBuilderExpression);
    property OnInternalChanged: TNotifyEvent read FOnInternalChanged write FOnInternalChanged;
  public
    constructor Create(AOwner: TPersistent);
    function Add: TTMSFNCFilterBuilderExpression; overload;
    function Add(AColumnName: string; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False; ACaseSensitive: Boolean = False): TTMSFNCFilterBuilderExpression; overload;
    function Add(AColumn: TTMSFNCFilterBuilderColumn; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False; ACaseSensitive: Boolean = False): TTMSFNCFilterBuilderExpression; overload;
    function Add(AColumnName: string; AValue: TTMSFNCValue): TTMSFNCFilterBuilderExpression; overload;
    function Add(AColumn: TTMSFNCFilterBuilderColumn; AValue: TTMSFNCValue): TTMSFNCFilterBuilderExpression; overload;
    function GetExpressionIndex(AExpression: TTMSFNCFilterBuilderExpression): Integer;
    procedure Assign(Source: TTMSFNCFilterBuilderExpressions);
    property Builder: TTMSFNCCustomFilterBuilder read GetBuilder;
  end;

  TTMSFNCFilterBuilderGroups = class;

  TTMSFNCFilterBuilderGroup = class(TPersistent)
  private
    FUpdateCount: Integer;
    FBuilder: TTMSFNCCustomFilterBuilder;
    FGroupOperator: TTMSFNCFilterBuilderGroupOperator;
    FOwnerGroups: TTMSFNCFilterBuilderGroups;
    FGroups: TTMSFNCFilterBuilderGroups;
    FExpressions: TTMSFNCFilterBuilderExpressions;
    FOnInternalChanged: TNotifyEvent;
    FDataPointer: Pointer;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
    FOnGetFilterBuilder: TTMSFNCGetFilterBuilderEvent;
    procedure SetGroupOperator(const AValue: TTMSFNCFilterBuilderGroupOperator);
    procedure SetGroups(const AValue: TTMSFNCFilterBuilderGroups);
    procedure SetExpressions(const AValue: TTMSFNCFilterBuilderExpressions);
    procedure RemoveExpression(AExpression: TTMSFNCFilterBuilderExpression);
    procedure RemoveGroup(AGroup: TTMSFNCFilterBuilderGroup);
    procedure RemoveThisGroup;
    procedure ChangeExpressionToGroup(AExpressionText: String; AExpression: TTMSFNCFilterBuilderExpression);
    procedure PopulateGroup(AGroups: TArray<TTMSFNCFilterBuilderGroup>; AExpressions: TArray<TTMSFNCFilterBuilderExpression>);
    function GetInternalFilterText(ADisplayNames: Boolean= False): string;
    function GetFilterText: string;
  protected
    procedure DoChanged;
    property OnInternalChanged: TNotifyEvent read FOnInternalChanged write FOnInternalChanged;
    procedure DoInternalGroupsChanged(Sender: TObject);
    procedure DoInternalExpressionsChanged(Sender: TObject);
    procedure ParseExpression(AExpression: string);
    function GetIndex: Integer;
    procedure BeginUpdate;
    procedure EndUpdate;
    procedure InitializeCreate(AGroupOperator: TTMSFNCFilterBuilderGroupOperator = fgoAND);
  public
    function GetBuilder: TTMSFNCCustomFilterBuilder;
    procedure DoGetFilterBuilder(var AFilterBuilder: TTMSFNCCustomFilterBuilder);
    constructor Create(AGroups: TTMSFNCFilterBuilderGroups); overload;
    constructor Create(ABuilder: TTMSFNCCustomFilterBuilder); overload;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    function GetGroupOperatorText: string;
    function ValidateFilter(AInputData: TTMSFNCFilterValidateInputRow): Boolean;
    function AddExpression: TTMSFNCFilterBuilderExpression; overload;
    function AddExpression(AColumnName: string; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False; ACaseSensitive: Boolean = False): TTMSFNCFilterBuilderExpression; overload;
    function AddExpression(AColumn: TTMSFNCFilterBuilderColumn; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False; ACaseSensitive: Boolean = False): TTMSFNCFilterBuilderExpression; overload;
    function AddExpression(AColumnName: string; AValue: TTMSFNCValue): TTMSFNCFilterBuilderExpression; overload;
    function AddExpression(AColumn: TTMSFNCFilterBuilderColumn; AValue: TTMSFNCValue): TTMSFNCFilterBuilderExpression; overload;
    function AddGroup(AGroupOperator: TTMSFNCFilterBuilderGroupOperator = fgoAND): TTMSFNCFilterBuilderGroup;
    function AddANDGroup: TTMSFNCFilterBuilderGroup;
    function AddORGroup: TTMSFNCFilterBuilderGroup;
    procedure Clear;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataString: String read FDataString write FDataString;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
    property FilterText: string read GetFilterText;
    property GroupOperator: TTMSFNCFilterBuilderGroupOperator read FGroupOperator write SetGroupOperator default fgoAND;
    property Groups: TTMSFNCFilterBuilderGroups read FGroups write SetGroups;
    property Expressions: TTMSFNCFilterBuilderExpressions read FExpressions write SetExpressions;
    property OnGetFilterBuilder: TTMSFNCGetFilterBuilderEvent read FOnGetFilterBuilder write FOnGetFilterBuilder;
  end;

  TTMSFNCFilterBuilderFilter = class(TTMSFNCFilterBuilderGroup);

  TTMSFNCFilterBuilderGroups = class(TObjectList<TTMSFNCFilterBuilderGroup>)
  private
    FBuilder: TTMSFNCCustomFilterBuilder;
    FParentGroup: TTMSFNCFilterBuilderGroup;
    FOnInternalChanged: TNotifyEvent;
  protected
    procedure DoInternalGroupsChanged(Sender: TObject);
    procedure DoInternalExpressionsChanged(Sender: TObject);
    procedure DoChanged;
    function GetGroupIndex(AGroup: TTMSFNCFilterBuilderGroup):Integer;
    function Add(AExpression: string): TTMSFNCFilterBuilderGroup; overload;
    property OnInternalChanged: TNotifyEvent read FOnInternalChanged write FOnInternalChanged;
  public
    constructor Create(AOwner: TPersistent);
    destructor Destroy; override;
    function Add: TTMSFNCFilterBuilderGroup; overload;
    function Add(AGroup: TTMSFNCFilterBuilderGroup): TTMSFNCFilterBuilderGroup; reintroduce; overload; virtual;
    function GetBuilder: TTMSFNCCustomFilterBuilder;
    procedure Assign(Source: TTMSFNCFilterBuilderGroups);
  end;

  TTMSFNCFilterBuilderFilterEvent = procedure(Sender: TObject; AFilterText: string) of object;
  TTMSFNCFilterBuilderGetColumnDataEvent = procedure(Sender: TObject; AColumnData: TTMSFNCFilterBuilderColumns) of object;
  TTMSFNCFilterBuilderGetGroupTextEvent = procedure(Sender: TObject; AGroup: TTMSFNCFilterBuilderGroup; var AText: string) of object;
  TTMSFNCFilterBuilderGetExpressionTextEvent = procedure(Sender: TObject; AExpression: TTMSFNCFilterBuilderExpression; var AText: string) of object;
  TTMSFNCFilterBuilderExpressionTextParsedEvent = procedure(Sender: TObject; AExpression: TTMSFNCFilterBuilderExpression; AText: string) of object;
  TTMSFNCFilterBuilderFilterTextParsedEvent = procedure(Sender: TObject; AFilter: TTMSFNCFilterBuilderGroup; AText: string) of object;
  TTMSFNCFilterBuilderGetFormatSettingsEvent = procedure(Sender: TObject; var AFormatSettings: TFormatSettings) of object;
  TTMSFNCFilterBuilderParseErrorEvent = procedure(Sender: TObject; AFilterText: string; AErrorPosition: Integer) of object;
  TTMSFNCFilterBuilderValidateGroupEvent = procedure(Sender: TObject; AGroup: TTMSFNCFilterBuilderGroup; AInputData: TArray<TTMSFNCValue>; var AValidationResult: Boolean; var AExitValidation: Boolean) of object;
  TTMSFNCFilterBuilderValidateFilterEvent = procedure(Sender: TObject; AInputData: TArray<TTMSFNCValue>; var AValidationResult: Boolean) of object;
  TTMSFNCFilterBuilderValidateExpressionEvent = procedure(Sender: TObject; AExpression: TTMSFNCFilterBuilderExpression; AInputValue: TTMSFNCValue; var AValidationResult: Boolean; var AExitValidation: Boolean) of object;
  TTMSFNCFilterBuilderFilterAllowEvent = procedure(Sender: TObject; var AAllow: Boolean) of object;

  TTMSFNCFilterBuilderParseFormatInfo = record
    ParenthesisOpen: string;
    ParenthesisClose: string;
    ColumnOpen: string;
    ColumnClose: string;
    AndOperator: string;
    OrOperator: string;
    NotOperator: string;
//    XOrOperator: string;
  end;

  TTMSFNCFilterBuilderExpressionOperatorFormat = record
    MultiCharWildCard: string;
    SingleCharWildCard: string;
    StartsWithOpen: string;
    StartsWithClose: string;
    EndsWithOpen: string;
    EndsWithClose: string;
    ContainsOpen: string;
    ContainsClose: string;
    NotContainsClose: string;
    EqualOpen: string;
    EqualClose: string;
    EqualStrOpen: string;
    EqualStrClose: string;
    NotEqualOpen: string;
    NotEqualClose: string;
    Empty: string;
    NotEmpty: string;
    LargerThanOpen: string;
    LargerThanClose: string;
    SmallerThanOpen: string;
    SmallerThanClose: string;
    LargerThanOrEqualOpen: string;
    LargerThanOrEqualClose: string;
    SmallerThanOrEqualOpen: string;
    SmallerThanOrEqualClose: string;
    StringDelimiter: string;
  end;

  TTMSFNCFilterBuilderExpressionCheckInfo = record
    IsValid: Boolean;
    ErrorParenthesis: Integer;
    ErrorOperatorPosition: Integer;
    ErrorOperatorMismatch: Integer;
    ErrorInvalidExpression: Integer;
  end;

  TTMSFNCFilterBuilderExpressionInformation = record
    IsEmpty: Boolean;
    IsGroup: Boolean;
    Column: TTMSFNCFilterBuilderColumn;
    ColumnName: String;
    ExpressionOperator: TTMSFNCFilterBuilderExpressionOperator;
    Value: TTMSFNCValue;
    ValueText: string;
    Inverted: Boolean;
  end;

  TTMSFNCCustomFilterBuilder = class(TPersistent)
  private
    FUpdateCount: Integer;
    FFilter: TTMSFNCFilterBuilderGroup;
    FParseFormat: TTMSFNCFilterBuilderParseFormat;
    FDataColumns: TTMSFNCFilterBuilderColumns;
    FOnChanged: TNotifyEvent;
    FOnGetFormatSettings: TTMSFNCFilterBuilderGetFormatSettingsEvent;
    FOnParseErrorParenthesis: TTMSFNCFilterBuilderParseErrorEvent;
    FOnParseErrorInvalidExpression: TTMSFNCFilterBuilderParseErrorEvent;
    FOnParseErrorOperatorMismatch: TTMSFNCFilterBuilderParseErrorEvent;
    FOnParseErrorOperatorPosition: TTMSFNCFilterBuilderParseErrorEvent;
    FOnParseError: TTMSFNCFilterBuilderFilterEvent;
    FOnColumnsChanged: TNotifyEvent;
    FOnParseFormatChanged: TNotifyEvent;
    FOnGetFilterText: TTMSFNCFilterBuilderGetGroupTextEvent;
    FOnGetGroupText: TTMSFNCFilterBuilderGetGroupTextEvent;
    FOnGetExpressionText: TTMSFNCFilterBuilderGetExpressionTextEvent;
    FOnExpressionTextParsed: TTMSFNCFilterBuilderExpressionTextParsedEvent;
    FOnFilterTextParsed: TTMSFNCFilterBuilderFilterTextParsedEvent;
    FOnValidateExpression: TTMSFNCFilterBuilderValidateExpressionEvent;
    FOnValidateFilter: TTMSFNCFilterBuilderValidateFilterEvent;
    FOnValidateGroup: TTMSFNCFilterBuilderValidateGroupEvent;
    FOnBeforeClearFilter: TTMSFNCFilterBuilderFilterAllowEvent;
    FOnAfterClearFilter: TNotifyEvent;
    FDataPointer: Pointer;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
    FFormatType: TTMSFNCFilterBuilderFormatType;
    FOnGetDisplayFilterText: TTMSFNCFilterBuilderGetGroupTextEvent;
    FOnInternalChanged: TNotifyEvent;
    FOnExpressionAdded: TTMSFNCFilterBuilderExpressionTextParsedEvent;
    procedure DoOnDataColumnNameChange(Sender: TObject; AColumn: TTMSFNCFilterBuilderColumn);
    procedure DoOnDataColumnRemove(Sender: TObject; AColumn: TTMSFNCFilterBuilderColumn);
    function GetFilterText: string;
    procedure SetFilter(const Value: TTMSFNCFilterBuilderGroup);
    procedure SetParseFormat(const Value: TTMSFNCFilterBuilderParseFormat);
    procedure SetDataColumns(const Value: TTMSFNCFilterBuilderColumns);
    function InternalCheckExpression(AExpression: string): TTMSFNCFilterBuilderExpressionCheckInfo;
    procedure SetFilterText(const Value: string);
    procedure SetFormatType(const Value: TTMSFNCFilterBuilderFormatType);
    function GetDisplayFilterText: string;
    procedure SetDisplayFilterText(const Value: string);
    function GetDataColumn(AName: string): TTMSFNCFilterBuilderColumn;
    function GetDataColumnType(AName: string): TTMSFNCFilterBuilderDataType;
    procedure SetDataColumnType(AName: string; const Value: TTMSFNCFilterBuilderDataType);
    function GetDataColumnDisplayName(AName: string): string;
    procedure SetDataColumnDisplayName(AName: string; const Value: string);
    function GetIsEmpty: Boolean;
  protected
    function GetExpressionOperatorFormat: TTMSFNCFilterBuilderExpressionOperatorFormat;
    procedure DoInternalFilterChanged(Sender: TObject);
    procedure DoInternalColumnsChanged(Sender: TObject);
    procedure DoInternalFormatChanged(Sender: TObject);
    procedure DoInternalChanged;
    procedure DoChanged; virtual;
    procedure DoColumnsChanged; virtual;
    procedure DoParseFormatChanged; virtual;
    procedure DoParseErrorParenthesis(AFilterText: string; AErrorPosition: Integer); virtual;
    procedure DoParseErrorOperatorPosition(AFilterText: string; AErrorPosition: Integer); virtual;
    procedure DoParseErrorOperatorMismatch(AFilterText: string; AErrorPosition: Integer); virtual;
    procedure DoParseErrorInvalidExpression(AFilterText: string; AErrorPosition: Integer); virtual;
    procedure DoParseError(AFilterText: string); virtual;
    procedure DoGetFormatSettings(var AFormatSettings: TFormatSettings); virtual;
    procedure DoGetFilterText(AFilter: TTMSFNCFilterBuilderGroup; var AText: string); virtual;
    procedure DoGetDisplayFilterText(AFilter: TTMSFNCFilterBuilderGroup; var AText: string); virtual;
    procedure DoGetGroupText(AGroup: TTMSFNCFilterBuilderGroup; var AText: string); virtual;
    procedure DoGetExpressionText(AExpression: TTMSFNCFilterBuilderExpression; var AText: string); virtual;
    procedure DoFilterExpressionTextParsed(AExpression: TTMSFNCFilterBuilderExpression; AExpressionText: string); virtual;
    procedure DoFilterTextParsed(AFilter: TTMSFNCFilterBuilderGroup; AExpressionText: string); virtual;
    procedure DoValidateGroup(AGroup: TTMSFNCFilterBuilderGroup; AInputData: TArray<TTMSFNCValue>; var AValidationResult: Boolean; var AExitValidation: Boolean); virtual;
    procedure DoValidateFilter(AInputData: TArray<TTMSFNCValue>; var AValidationResult: Boolean); virtual;
    procedure DoValidateExpression(AExpression: TTMSFNCFilterBuilderExpression; AInputValue: TTMSFNCValue; var AValidationResult: Boolean; var AExitValidation: Boolean); virtual;
    procedure DoBeforeClearFilter(var AAllow: Boolean); virtual;
    procedure DoAfterClearFilter; virtual;
    procedure SetDelphiDataSetFormat(AStringDelimiter: Char = ''''; ASingleWildCard: Char = '_'; AMultiCharWildCard: Char = '%');
    procedure SetUniversalFilterExpressionsFormat(AStringDelimiter: Char = '"'; ASingleWildCard: Char = '?'; AMultiCharWildCard: Char = '*');
    procedure HandleValidationErrors(AText: string; ACheckInfo: TTMSFNCFilterBuilderExpressionCheckInfo);
    procedure DoOnExpressionAdded(AExpression:TTMSFNCFilterBuilderExpression);
    procedure DoExpressionAdded(AExpression:TTMSFNCFilterBuilderExpression);
    property OnInternalChanged: TNotifyEvent read FOnInternalChanged write FOnInternalChanged;
    property ParseFormat: TTMSFNCFilterBuilderParseFormat read FParseFormat write SetParseFormat;
    property Filter: TTMSFNCFilterBuilderGroup read FFilter write SetFilter;
    property FilterText: string read GetFilterText write SetFilterText;
    property DisplayFilterText: string read GetDisplayFilterText write SetDisplayFilterText;
    property DataColumn[AName: string]: TTMSFNCFilterBuilderColumn read GetDataColumn;
    property DataColumnType[AName: string]: TTMSFNCFilterBuilderDataType read GetDataColumnType write SetDataColumnType;
    property DataColumnDisplayName[AName: string]: string read GetDataColumnDisplayName write SetDataColumnDisplayName;
    property DataColumns: TTMSFNCFilterBuilderColumns read FDataColumns write SetDataColumns;
    property FormatType: TTMSFNCFilterBuilderFormatType read FFormatType write SetFormatType default fftDelphiDataSet;
    property IsEmpty: Boolean read GetIsEmpty;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
    property OnColumnsChanged: TNotifyEvent read FOnColumnsChanged write FOnColumnsChanged;
    property OnParseFormatChanged: TNotifyEvent read FOnParseFormatChanged write FOnParseFormatChanged;
    property OnGetFormatSettings: TTMSFNCFilterBuilderGetFormatSettingsEvent read FOnGetFormatSettings write FOnGetFormatSettings;
    property OnParseErrorParenthesis: TTMSFNCFilterBuilderParseErrorEvent read FOnParseErrorParenthesis write FOnParseErrorParenthesis;
    property OnParseErrorOperatorPosition: TTMSFNCFilterBuilderParseErrorEvent read FOnParseErrorOperatorPosition write FOnParseErrorOperatorPosition;
    property OnParseErrorOperatorMismatch: TTMSFNCFilterBuilderParseErrorEvent read FOnParseErrorOperatorMismatch write FOnParseErrorOperatorMismatch;
    property OnParseErrorInvalidExpression: TTMSFNCFilterBuilderParseErrorEvent read FOnParseErrorInvalidExpression write FOnParseErrorInvalidExpression;
    property OnParseError: TTMSFNCFilterBuilderFilterEvent read FOnParseError write FOnParseError;
    property OnGetFilterText: TTMSFNCFilterBuilderGetGroupTextEvent read FOnGetFilterText write FOnGetFilterText;
    property OnGetDisplayFilterText: TTMSFNCFilterBuilderGetGroupTextEvent read FOnGetDisplayFilterText write FOnGetDisplayFilterText;
    property OnGetGroupText: TTMSFNCFilterBuilderGetGroupTextEvent read FOnGetGroupText write FOnGetGroupText;
    property OnGetExpressionText: TTMSFNCFilterBuilderGetExpressionTextEvent read FOnGetExpressionText write FOnGetExpressionText;
    property OnExpressionTextParsed: TTMSFNCFilterBuilderExpressionTextParsedEvent read FOnExpressionTextParsed write FOnExpressionTextParsed;
    property OnFilterTextParsed: TTMSFNCFilterBuilderFilterTextParsedEvent read FOnFilterTextParsed write FOnFilterTextParsed;
    property OnValidateGroup: TTMSFNCFilterBuilderValidateGroupEvent read FOnValidateGroup write FOnValidateGroup;
    property OnValidateFilter: TTMSFNCFilterBuilderValidateFilterEvent read FOnValidateFilter write FOnValidateFilter;
    property OnValidateExpression: TTMSFNCFilterBuilderValidateExpressionEvent read FOnValidateExpression write FOnValidateExpression;
    property OnBeforeClearFilter: TTMSFNCFilterBuilderFilterAllowEvent read FOnBeforeClearFilter write FOnBeforeClearFilter;
    property OnAfterClearFilter: TNotifyEvent read FOnAfterClearFilter write FOnAfterClearFilter;
    property OnExpressionAdded: TTMSFNCFilterBuilderExpressionTextParsedEvent read FOnExpressionAdded write FOnExpressionAdded;
  public
    constructor Create;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    function GetExpressionInformation(AExpression: string): TTMSFNCFilterBuilderExpressionInformation;
    function GetExpressionText(AColumnName: String; ADataType: TTMSFNCFilterBuilderDataType; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValueText: string; AValue: TTMSFNCValue; AInverted: Boolean): string; overload;
    function GetExpressionText(AColumnName: String; ADataType: TTMSFNCFilterBuilderDataType; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValueText: string; AInverted: Boolean): string; overload;
    function DeleteGroup(AGroup: TTMSFNCFilterBuilderGroup): Boolean;
    function DeleteExpression(AExpression: TTMSFNCFilterBuilderExpression): Boolean;
    function GetFormatSettings: TFormatSettings;
    function GetColumnByName(AColumnName: string): TTMSFNCFilterBuilderColumn;
    function GetColumnByDisplayName(AColumnDisplayName: string): TTMSFNCFilterBuilderColumn;
    function GetOperatorText(AGroupOperator: TTMSFNCFilterBuilderGroupOperator): string; overload;
    function GetOperatorText(AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator): string; overload;
    function PopulateFilter(AGroups: TArray<TTMSFNCFilterBuilderGroup>; AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
    function PopulateFilterGroups(AGroups: TArray<TTMSFNCFilterBuilderGroup>): TTMSFNCFilterBuilderGroup;
    function PopulateFilterExpressions(AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
    function isExpressionValid(AExpression: string): Boolean;
    function ValidateFilterRow(AInputData: TArray<TTMSFNCValue>): Boolean;
    function ValidateFilterArray(AInputArray: TTMSFNCFilterValidateInputArray; AByRow: Boolean = False): TTMSFNCFilterValidateOutputArray;
    function AddDataColumn: TTMSFNCFilterBuilderColumn; overload;
    function AddDataColumn(AName: string; ADataType: TTMSFNCFilterBuilderDataType = fdtAutomatic; ADisplayName: string = ''): TTMSFNCFilterBuilderColumn; overload;
    function ParseExpression(AFilterText: string): Boolean;
    procedure ClearFilter;
    procedure BeginUpdate;
    procedure EndUpdate;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataString: String read FDataString write FDataString;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
  end;

  TTMSFNCFilterBuilder = class(TTMSFNCCustomFilterBuilder)
  public
    property IsEmpty;
    property DataColumn;
    property DataColumnDisplayName;
    property DataColumnType;
  published
    property ParseFormat;
    property FilterText;
    property DisplayFilterText;
    property Filter;
    property DataColumns;
    property FormatType;
    property OnChanged;
    property OnColumnsChanged;
    property OnParseFormatChanged;
    property OnGetFormatSettings;
    property OnParseErrorParenthesis;
    property OnParseErrorOperatorPosition;
    property OnParseErrorOperatorMismatch;
    property OnParseErrorInvalidExpression;
    property OnParseError;
    property OnGetFilterText;
    property OnGetGroupText;
    property OnGetExpressionText;
    property OnExpressionTextParsed;
    property OnFilterTextParsed;
    property OnValidateGroup;
    property OnValidateFilter;
    property OnValidateExpression;
    property OnExpressionAdded;
  end;

  TTMSFNCFilterBuilderTestValidateInput = record
    Name: string;
    DataType: TTMSFNCFilterBuilderDataType;
    Value: TTMSFNCValue
  end;

  function MatchStrings(aPattern: string; AExpression: string; AMultiCharWildCard: string = ''; ASingleCharWildCard: string = ''): Boolean;
  function CreateExpression(AColumn: TTMSFNCFilterBuilderColumn; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False): TTMSFNCFilterBuilderExpression; overload;
  function CreateExpression(AColumnName: string; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False): TTMSFNCFilterBuilderExpression; overload;
  function CreateORGroup(AGroups: TArray<TTMSFNCFilterBuilderGroup>; AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
  function CreateANDGroup(AGroups: TArray<TTMSFNCFilterBuilderGroup>; AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
  function ParseValueTextToFNCValue(AText: string; ADataType: TTMSFNCFilterBuilderDataType; AFormatSettings: TFormatSettings): TTMSFNCValue;

  function GetFilterBuilderTextForExpressionOperator(AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AUILanguage: TTMSFNCFilterBuilderCustomUILanguage = nil): string;
  function GetFilterBuilderExpressionOperatorFoundForText(AText: string; var AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AUILanguage: TTMSFNCFilterBuilderCustomUILanguage = nil): Boolean;
  function GetFilterBuilderExpressionText(AColumnName: String; ADataType: TTMSFNCFilterBuilderDataType; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValueText: string;
    AValue: TTMSFNCValue; AInverted: Boolean; AFormatSettings: TFormatSettings; AParseFormat: TTMSFNCFilterBuilderParseFormat; AIsDataSet:Boolean): string;
  function CheckFilterBuilderExpression(AExpression: string; AParseFormat: TTMSFNCFilterBuilderParseFormat; AIsDataSet:Boolean): TTMSFNCFilterBuilderExpressionCheckInfo;
  procedure SetFilterFormatUniversalFilterExpressions(AParseFormat: TTMSFNCFilterBuilderParseFormat; AStringDelimiter: Char = '"'; ASingleWildCard: Char = '?'; AMultiCharWildCard: Char = '*');
  procedure SetFilterFormatDelphiDataSet(AParseFormat: TTMSFNCFilterBuilderParseFormat; AStringDelimiter: Char = ''''; ASingleWildCard: Char = '_'; AMultiCharWildCard: Char = '%');
  function  GetGroupOperatorText(AGroupOperator: TTMSFNCFilterBuilderGroupOperator; AParseFormat: TTMSFNCFilterBuilderParseFormat): string;

  function TMSFNCFilterBuilder: TTMSFNCFilterBuilder;

implementation

uses
  StrUtils, DateUtils;

var
  FTMSFNCFilterBuilder: TTMSFNCFilterBuilder = nil;

function TMSFNCFilterBuilder: TTMSFNCFilterBuilder;
begin
  if FTMSFNCFilterBuilder = nil then
    FTMSFNCFilterBuilder := TTMSFNCFilterBuilder.Create;
  Result := FTMSFNCFilterBuilder;
end;

function GetStringStartIndex: Integer;
begin
  Result := 0;
end;

function SetUpperCase(AText: string): string;
begin
  //Check for case sensititve
  Result := UpperCase(AText);
end;

function CustomSplit(const AString: string; ADelimiters: TArray<string>; const AKeepDelimiterChars: Boolean = False): TArray<string>;
var
  posUpdated: Boolean;
  I, stIdx, curPos, lastPos, delCnt: Integer;
begin
  stIdx := 1;
  curPos := stIdx;
  delCnt := 0;
  lastPos := 0;
  SetLength(Result, 0);

  while curPos <= Length(AString) do
  begin
    posUpdated := False;
    for I := 0 to Length(ADelimiters) - 1 do
    begin
      delCnt := 1;
      if  (Length(ADelimiters[I]) > 0) and (AString[curPos] = ADelimiters[I][stIdx]) then
      begin
        if Length(ADelimiters[I]) > 1 then
        begin
          delCnt := Length(ADelimiters[I]);

          if AString.Substring(curPos, delCnt) <> ADelimiters[I] then
            Break;
         end;

        if curPos - lastPos > delCnt then
        begin
          SetLength(Result, Length(Result) + 1);
          Result[Length(Result) - 1] := AString.Substring(lastPos, curPos - lastPos - delCnt);
          lastPos := curPos - delCnt;
        end;

        if AKeepDelimiterChars then
        begin
          SetLength(Result, Length(Result) + 1);
          Result[Length(Result) - 1] := AString.Substring(lastPos, curPos - lastPos);
        end;

        lastPos := curPos;
        Inc(curPos, delCnt);

        posUpdated := True;

        Break;
      end;
    end;

    if not posUpdated then
      Inc(curPos);
  end;

  if lastPos < Length(AString) then
  begin
    SetLength(Result, Length(Result) + 1);
    Result[Length(Result) - 1] := AString.Substring(lastPos, curPos + delCnt - lastPos);
  end;
end;

function GetDefaultParseFormatInfo(ADataSet: Boolean = True): TTMSFNCFilterBuilderParseFormatInfo;
begin
    Result.ParenthesisOpen := '(';
    Result.ParenthesisClose := ')';

    Result.ColumnOpen := '[';
    Result.ColumnClose := ']';

    Result.AndOperator := 'AND';
    Result.OrOperator := 'OR';
    Result.NotOperator := 'NOT';
//    Result.XOrOperator := 'XOR';
end;

function GetDefaultExpressionOperatorFormat(ADataSet: Boolean = True): TTMSFNCFilterBuilderExpressionOperatorFormat;
begin
  if ADataSet then
  begin
    Result.StringDelimiter := '''';
    Result.MultiCharWildCard := '%';
    Result.SingleCharWildCard := '_';
    Result.StartsWithOpen := 'LIKE ''';
    Result.StartsWithClose := '%''';
    Result.EndsWithOpen := 'LIKE ''%';
    Result.EndsWithClose := '''';
    Result.ContainsOpen := 'LIKE ''%';
    Result.ContainsClose := '%''';
    Result.NotContainsClose := '%''';
    Result.EqualOpen := '= ';
    Result.EqualClose := '';
    Result.EqualStrOpen := 'LIKE ''';
    Result.EqualStrClose := '''';
    Result.NotEqualOpen := '<>';
    Result.NotEqualClose := '';
    Result.Empty := 'is NULL';
    Result.NotEmpty := 'is not NULL';
    Result.LargerThanOpen := '>';
    Result.LargerThanClose := '';
    Result.SmallerThanOpen := '<';
    Result.SmallerThanClose := '';
    Result.LargerThanOrEqualOpen := '>=';
    Result.LargerThanOrEqualClose := '';
    Result.SmallerThanOrEqualOpen := '<=';
    Result.SmallerThanOrEqualClose := '';
  end
  else
  begin
    Result.StringDelimiter := '"';
    Result.MultiCharWildCard := '*';
    Result.SingleCharWildCard := '?';
    Result.StartsWithOpen := '= "';
    Result.StartsWithClose := '*"';
    Result.EndsWithOpen := '= "*';
    Result.EndsWithClose := '"';
    Result.ContainsOpen := '= "*';
    Result.ContainsClose := '*"';
    Result.NotContainsClose := '*"';
    Result.EqualOpen := '=';
    Result.EqualClose := '';
    Result.EqualStrOpen := '=';
    Result.EqualStrClose := '';
    Result.NotEqualOpen := '<>';
    Result.NotEqualClose := '';
    Result.Empty := '= ""';
    Result.NotEmpty := '="*"';
    Result.LargerThanOpen := '>';
    Result.LargerThanClose := '';
    Result.SmallerThanOpen := '<';
    Result.SmallerThanClose := '';
    Result.LargerThanOrEqualOpen := '>=';
    Result.LargerThanOrEqualClose := '';
    Result.SmallerThanOrEqualOpen := '<=';
    Result.SmallerThanOrEqualClose := '';
    Result.SingleCharWildCard := '?';
    Result.MultiCharWildCard := '*';
    Result.StringDelimiter := '"';
  end;
end;

function GetParseFormatInfo(AParseFormat: TTMSFNCFilterBuilderParseFormat = nil): TTMSFNCFilterBuilderParseFormatInfo;
var
  par, col: TArray<string>;
begin
  if Assigned(AParseFormat) then
  begin
    par := AParseFormat.GroupFormat.Split(['#GROUP']);
    col := AParseFormat.ColumnFormat.Split(['#COLUMN']);

    Result.ParenthesisOpen := par[0];
    if Length(par) > 1 then
      Result.ParenthesisClose := par[1];

    Result.ColumnOpen := col[0];
    if Length(col) > 1 then
      Result.ColumnClose := col[1];

    Result.AndOperator := AParseFormat.AndOperator;
    Result.OrOperator := AParseFormat.OrOperator;
    Result.NotOperator := AParseFormat.NotOperator;
  //  Result.XOrOperator := FParseFormat.XOrOperator;

    par := [];
    col := [];
  end
  else
  begin
    Result := GetDefaultParseFormatInfo;
  end;
end;

function GetGroupOperatorText(AGroupOperator: TTMSFNCFilterBuilderGroupOperator; AParseFormat: TTMSFNCFilterBuilderParseFormat): string;
begin
  case AGroupOperator of
    fgoAND: Result := AParseFormat.AndOperator;
    fgoOR: Result := AParseFormat.OrOperator;
//    fgoXOR: Result := FParseFormat.XOrOperator;
  end;
end;

procedure SetFilterFormatUniversalFilterExpressions(AParseFormat: TTMSFNCFilterBuilderParseFormat; AStringDelimiter: Char = '"'; ASingleWildCard: Char = '?'; AMultiCharWildCard: Char = '*');
begin
  AParseFormat.BeginUpdate;

  AParseFormat.FStringDelimiter := AStringDelimiter;
  AParseFormat.FSingleCharWildCard := ASingleWildCard;
  AParseFormat.FMultiCharWildCard := AMultiCharWildCard;

  AParseFormat.FAndOperator := '&';
  AParseFormat.FColumnFormat := '[#COLUMN]';
  AParseFormat.FContainsOperator := '= ' + AStringDelimiter + AMultiCharWildCard + '#VALUE' + AMultiCharWildCard + AStringDelimiter;
//  AParseFormat.FNotContainsOperator := '<> '+ AStringDelimiter + AMultiCharWildCard + '#VALUE' + AMultiCharWildCard + AStringDelimiter;
  AParseFormat.FStartsWithOperator := '= ' + AStringDelimiter + '#VALUE' + AMultiCharWildCard + AStringDelimiter;
  AParseFormat.FEndsWithOperator := '= ' + AStringDelimiter + AMultiCharWildCard + '#VALUE' + AStringDelimiter;
  AParseFormat.FEmptyOperator := '= ' + AStringDelimiter + AStringDelimiter;
  AParseFormat.FNotEmptyOperator := '= ' + AStringDelimiter + AMultiCharWildCard + AStringDelimiter;

  AParseFormat.FEqualOperator := '= #VALUE';
  AParseFormat.FEqualStrOperator := '= ' + AStringDelimiter + '#VALUE'+ AStringDelimiter;
  AParseFormat.FLargerThanOrEqualOperator := '>= #VALUE';
  AParseFormat.FLargerThanOperator := '> #VALUE';
  AParseFormat.FGroupFormat := '(#GROUP)';
  AParseFormat.FSmallerThanOrEqualOperator := '<= #VALUE';
  AParseFormat.FSmallerThanOperator := '< #VALUE';
  AParseFormat.FNotEqualOperator := '!= #VALUE';
  AParseFormat.FOrOperator := '|';
  AParseFormat.FNotOperator := 'NOT';
//  AParseFormat.FXOrOperator := 'XOR';
  AParseFormat.EndUpdate;
end;

procedure SetFilterFormatDelphiDataSet(AParseFormat: TTMSFNCFilterBuilderParseFormat; AStringDelimiter: Char = ''''; ASingleWildCard: Char = '_'; AMultiCharWildCard: Char = '%');
begin
  AParseFormat.BeginUpdate;

  AParseFormat.FStringDelimiter := AStringDelimiter;
  AParseFormat.FSingleCharWildCard := ASingleWildCard;
  AParseFormat.FMultiCharWildCard := AMultiCharWildCard;

  AParseFormat.FGroupFormat := '(#GROUP)';
  AParseFormat.FAndOperator := 'AND';
  AParseFormat.FColumnFormat := '[#COLUMN]';
  AParseFormat.FContainsOperator := 'LIKE ' + AStringDelimiter + AMultiCharWildCard + '#VALUE' + AMultiCharWildCard + AStringDelimiter;
  AParseFormat.FStartsWithOperator := 'LIKE ' + AStringDelimiter + '#VALUE' + AMultiCharWildCard + AStringDelimiter;
  AParseFormat.FEndsWithOperator := 'LIKE ' + AStringDelimiter + AMultiCharWildCard + '#VALUE' + AStringDelimiter;
//  AParseFormat.FNotContainsOperator := 'not ' + FParseFormat.FColumnFormat + ' LIKE ' + AStringDelimiter + AMultiCharWildCard + '#VALUE' + AMultiCharWildCard + AStringDelimiter;
  AParseFormat.FEmptyOperator := 'IS NULL';
  AParseFormat.FNotEmptyOperator := 'IS NOT NULL';

  AParseFormat.FEqualOperator := '= #VALUE';
  AParseFormat.FEqualStrOperator := 'LIKE ' + AStringDelimiter + '#VALUE' + AStringDelimiter;
  AParseFormat.FLargerThanOrEqualOperator := '>= #VALUE';
  AParseFormat.FLargerThanOperator := '> #VALUE';
  AParseFormat.FSmallerThanOrEqualOperator := '<= #VALUE';
  AParseFormat.FSmallerThanOperator := '< #VALUE';

  AParseFormat.FNotEqualOperator := '<> #VALUE';
  AParseFormat.FOrOperator := 'OR';
  AParseFormat.FNotOperator := 'NOT';
//  AParseFormat.FXOrOperator := 'XOR';
  AParseFormat.EndUpdate;
end;

function CheckFilterBuilderExpression(AExpression: string; AParseFormat: TTMSFNCFilterBuilderParseFormat; AIsDataSet:Boolean): TTMSFNCFilterBuilderExpressionCheckInfo;
var
  pfi: TTMSFNCFilterBuilderParseFormatInfo;
  ExpArr: TArray<string>;
  part, prevPart, original: string;
  I, parCnt, colCnt: Integer;
  newGroup, firstOperator: Boolean;
  operatorStack: TStack<TTMSFNCFilterBuilderGroupOperator>;
  gop: TTMSFNCFilterBuilderGroupOperator;

  function IsOperator(const Token: string): Boolean;
  begin
    Result := SetUpperCase(Token).Equals(SetUpperCase(AParseFormat.AndOperator))
              or SetUpperCase(Token).Equals(SetUpperCase(AParseFormat.OrOperator));
//              or SetUpperCase(Token).Equals(SetUpperCase(ParseFormat.XorOperator));
  end;

  function GetOperator(const Token: string): TTMSFNCFilterBuilderGroupOperator;
  begin
    if SetUpperCase(Token).Equals(SetUpperCase(AParseFormat.AndOperator)) then
      Result := fgoAND
//    else if SetUpperCase(Token).Equals(SetUpperCase(ParseFormat.XorOperator)) then
//      Result := fgoXOR
    else
      Result := fgoOR;
  end;
begin
  Result.IsValid := True;
  Result.ErrorParenthesis := -1;
  Result.ErrorOperatorPosition := -1;
  Result.ErrorOperatorMismatch := -1;
  Result.ErrorInvalidExpression := -1;

  if Trim(AExpression) = '' then
    Exit;

  pfi := GetParseFormatInfo(AParseFormat);
  part := '';
  prevPart := '';
  original := '';

  operatorStack := TStack<TTMSFNCFilterBuilderGroupOperator>.Create;

  newGroup := False;
  firstOperator := True;
  parCnt := 0;
  colCnt := 0;

  try
    ExpArr := CustomSplit(AExpression, [pfi.ParenthesisOpen, pfi.ParenthesisClose, pfi.ColumnOpen, pfi.ColumnClose,' '], True);

    for I := 0 to Length(ExpArr) - 1 do
    begin
      if Trim(ExpArr[I]) <> '' then
      begin
        part := Trim(ExpArr[I]);
        if part.StartsWith(pfi.ParenthesisOpen) and (colCnt = 0) then
        begin
          Inc(parCnt);
          newGroup := True;
        end
        else if (part = pfi.ParenthesisClose) and (colCnt = 0) then
        begin
          if IsOperator(prevPart) then      //Can't close group after operator
          begin
            Result.IsValid := False;
            Result.ErrorOperatorPosition := Length(original);

            Exit;
          end;

          Dec(parCnt);

          if (parCnt < 0) then          //More closing then opening parentheses
          begin
            Result.IsValid := False;
            Result.ErrorParenthesis := Length(original);

            Exit;
          end
          else if (operatorStack.Count > 1) then
            operatorStack.Pop; //Get to previous group operator
        end
        else if part = pfi.ColumnOpen then
          Inc(colCnt)
        else if part = pfi.ColumnClose then
          Dec(colCnt)
        else if IsOperator(part) then
        begin
          gop := GetOperator(part);
          if newGroup then                                   //If open parenthesis passed
          begin
            operatorStack.Push(gop);
            newGroup := False;
          end
          else if firstOperator then                         //If it is the first operator of main group
          begin
            operatorStack.Push(gop);
            firstOperator := False;
          end
          else if gop <> operatorStack.Peek then            //Check for different operators in same group
          begin
            Result.IsValid := False;
            Result.ErrorOperatorMismatch := Length(original);

            Exit;
          end;
        end;

        prevPart := part;
      end;
      original := original + ExpArr[I];
    end;
  finally
    operatorStack.Free;
  end;

  if IsOperator(prevPart) then  //Expression can't end on operator
  begin
    Result.IsValid := False;
    Result.ErrorParenthesis := Length(original);

    Exit;
  end;

  if parCnt <> 0 then  //Combination of parentheses not correct
  begin
    Result.IsValid := False;
    Result.ErrorOperatorPosition := Length(original);

    Exit;
  end;

  if colCnt > 0 then //Combination of column indicators not correct
  begin
    Result.IsValid := False;
    Result.ErrorInvalidExpression := Length(original);

    Exit;
  end;
end;

function GetFilterBuilderExpressionText(AColumnName: String; ADataType: TTMSFNCFilterBuilderDataType; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValueText: string;
  AValue: TTMSFNCValue; AInverted: Boolean; AFormatSettings: TFormatSettings; AParseFormat: TTMSFNCFilterBuilderParseFormat; AIsDataSet:Boolean): string;
var
  f, o, v, sd: string;
  inv: Boolean;
  {$HINTS OFF}
  {$IF COMPILERVERSION <= 34}  
  d: Double;
  {$IFEND}
  {$HINTS ON}
begin
  v := '';
  inv := AInverted;
  sd := AParseFormat.StringDelimiter;

  if not ValueIsEmpty(AValue) and (ValueIsString(AValue) and  (ValueToString(AValue) <> '')) then
  begin
    if not (AExpressionOperator in [feoEmpty, feoNotEmpty]) then
    begin
      case ADataType of
        fdtNumber: v := ValueToInt64(AValue, AFormatSettings).ToString;
        fdtFloat: v := FloatToStr(ValueToFloat(AValue, AFormatSettings), AFormatSettings);
        fdtDateTime: v := DateTimeToStr(ValueToDateTime(AValue, AFormatSettings), AFormatSettings);
        fdtDate: v := DateToStr(ValueToDateTime(AValue, AFormatSettings), AFormatSettings);
        fdtTime: v := TimeToStr(ValueToDateTime(AValue, AFormatSettings), AFormatSettings);
        fdtBoolean: v := BoolToStr(ValueToBoolean(AValue, AFormatSettings), True);
        fdtText:
        begin
          if (AValueText <> '') and not ValueIsEmpty(AValue) then
              v := AValueText
          else
            v := ValueToString(AValue);
        end
        else
          v := ValueToString(AValue);
      end;
    end;
  end
  else
  begin
    v := AValueText;
	{$HINTS OFF}
    {$IF COMPILERVERSION <= 34}
    if TryStrToFloat(AValueText, d, AFormatSettings) then
    begin
      case ADataType of
        fdtDateTime: v := DateTimeToStr(d, AFormatSettings);
        fdtDate: v := DateToStr(d, AFormatSettings);
        fdtTime: v := TimeToStr(d, AFormatSettings);
      end;
    end;
    {$IFEND}
	{$HINTS ON}
  end;

  o := '';
  case AExpressionOperator of
    feoNotEmpty: o := AParseFormat.NotEmptyOperator;
    feoEmpty: o := AParseFormat.EmptyOperator;
    feoContains: o := AParseFormat.ContainsOperator;
    feoStartsWith: o := AParseFormat.StartsWithOperator;
    feoEndsWith: o := AParseFormat.EndsWithOperator;
    feoEqual:
    begin
      if (AParseFormat.FEqualStrOperator <> '') and ((ADataType = fdtText) or
          (ADataType = fdtAutomatic)  and (Pos(AParseFormat.MultiCharWildCard, v) > 0) or (Pos(AParseFormat.SingleCharWildCard, v) > 0) or (v.StartsWith(sd) and v.EndsWith(sd))) then
        o := AParseFormat.FEqualStrOperator
      else
        o := AParseFormat.EqualOperator;
    end;
    feoNotEqual:
    begin
      if (ADataType = fdtText) or ((ADataType = fdtAutomatic) and (Pos(AParseFormat.MultiCharWildCard, v) > 0) or (Pos(AParseFormat.SingleCharWildCard, v) > 0)) and (AParseFormat.FEqualStrOperator <> '') then
      begin
        inv := True;
        o := AParseFormat.FEqualStrOperator;
      end
      else
        o := AParseFormat.NotEqualOperator;
    end;
    feoLargerThan: o := AParseFormat.LargerThanOperator;
    feoLargerThanOrEqual: o := AParseFormat.LargerThanOrEqualOperator;
    feoSmallerThan: o := AParseFormat.SmallerThanOperator;
    feoSmallerThanOrEqual: o := AParseFormat.SmallerThanOrEqualOperator;
  end;

  if Pos(AParseFormat.ColumnFormat, o) > 0 then
  begin
    f := o.Replace('#COLUMN', AColumnName);
  end
  else
  begin
    f := AParseFormat.ColumnFormat.Replace('#COLUMN', AColumnName);
    f := f + ' ' + o;
  end;

  if inv then
    f := AParseFormat.NotOperator + ' ' + f;

  if ((ADataType = fdtText) or (AIsDataSet and (ADataType in [fdtDateTime, fdtDate, fdtTime]))) and not (v.StartsWith(sd) and v.EndsWith(sd)) and (Pos(sd, o) = 0) then
    f := f.Replace('#VALUE', sd + v + sd)
  else if (ADataType in [fdtAutomatic, fdtText]) and (v.StartsWith(sd) and v.EndsWith(sd)) and (Pos(sd + '#VALUE' + sd, o) > 0) then
    f := f.Replace(sd + '#VALUE' + sd, v)
  else
    f := f.Replace('#VALUE', v);

  Result := f;
end;

function GetFilterBuilderTextForExpressionOperator(AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AUILanguage: TTMSFNCFilterBuilderCustomUILanguage = nil): string;
var
  la: TTMSFNCFilterBuilderCustomUILanguage;
begin
  if Assigned(AUILanguage) then
    la := AUILanguage
  else
    la := TTMSFNCFilterBuilderCustomUILanguage.Create;

  try
    case AExpressionOperator of
    feoStartsWith: Result := la.StartsWithOperatorText;
    feoEndsWith: Result := la.EndsWithOperatorText;
    feoContains: Result := la.ContainsOperatorText;
    feoEqual: Result := la.EqualOperatorText;
    feoNotEqual: Result := la.NotEqualOperatorText;
    feoEmpty: Result := la.EmptyOperatorText;
    feoNotEmpty: Result := la.NotEmptyOperatorText;
    feoLargerThan: Result := la.LargerThanOperatorText;
    feoSmallerThan: Result := la.SmallerThanOperatorText;
    feoLargerThanOrEqual: Result := la.LargerThanOrEqualOperatorText;
    feoSmallerThanOrEqual: Result := la.SmallerThanOrEqualOperatorText;
    else
      Result := 'UNKNOWN OPERATOR';
  end;
  finally
    if not Assigned(AUILanguage) then
      FreeAndNil(la);
  end;
end;

function GetFilterBuilderExpressionOperatorFoundForText(AText: string; var AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AUILanguage: TTMSFNCFilterBuilderCustomUILanguage = nil): Boolean;
var
  la: TTMSFNCFilterBuilderCustomUILanguage;
begin
  if Assigned(AUILanguage) then
    la := AUILanguage
  else
    la := TTMSFNCFilterBuilderCustomUILanguage.Create;

  Result := True;

  if Trim(AText) = la.StartsWithOperatorText then
    AExpressionOperator := feoStartsWith
  else if Trim(AText) = Trim(la.EndsWithOperatorText) then
    AExpressionOperator := feoEndsWith
  else if Trim(AText) = Trim(la.ContainsOperatorText) then
    AExpressionOperator := feoContains
  else if Trim(AText) = Trim(la.EqualOperatorText) then
    AExpressionOperator := feoEqual
  else if Trim(AText) = Trim(la.NotEqualOperatorText) then
    AExpressionOperator := feoNotEqual
  else if Trim(AText) = Trim(la.EmptyOperatorText) then
    AExpressionOperator := feoEmpty
  else if Trim(AText) = Trim(la.NotEmptyOperatorText) then
    AExpressionOperator := feoNotEmpty
  else if Trim(AText) = Trim(la.LargerThanOperatorText) then
    AExpressionOperator := feoLargerThan
  else if Trim(AText) = Trim(la.SmallerThanOperatorText) then
    AExpressionOperator := feoSmallerThan
  else if Trim(AText) = Trim(la.LargerThanOrEqualOperatorText) then
    AExpressionOperator := feoLargerThanOrEqual
  else if Trim(AText) = Trim(la.SmallerThanOrEqualOperatorText) then
    AExpressionOperator := feoSmallerThanOrEqual
  else
    Result := False;
end;

function SplitExpression(ASplitExpression: string; AParseFormat: TTMSFNCFilterBuilderParseFormatInfo): TArray<string>;
var
  sp: TArray<string>;
  I: Integer;
  st: string;
  ParCnt, ColCnt: Integer;

  procedure AddTextExpression;
  begin
    SetLength(sp, Length(sp) + 1);
    sp[Length(sp) - 1] := st;
    st := '';
  end;
begin

  Result := [];
  sp := [];

  ParCnt := 0;
  ColCnt := 0;

  try
    {$IFDEF ZEROSTRINGINDEX}
    for I := 0 to Length(ASplitExpression) do
    {$ELSE}
    for I := 1 to Length(ASplitExpression) do
    {$ENDIF}
    begin
      if ASplitExpression[I] = AParseFormat.ParenthesisClose then
      begin
        st := st + ASplitExpression[I];
        if ColCnt = 0 then
        begin
          Dec(ParCnt);
          if ParCnt = 0 then
            AddTextExpression;
        end;
      end
      else if ASplitExpression[I] = AParseFormat.ParenthesisOpen  then
      begin
        if ColCnt > 0 then
          st := st  + ASplitExpression[I]
        else
        begin
          Inc(ParCnt);
          if ParCnt > 0 then
            st := st  + ASplitExpression[I];
        end
      end
      else if ASplitExpression[I] = AParseFormat.ColumnClose then
      begin
        Dec(ColCnt);
        st := st + ASplitExpression[I];
        if (ColCnt = 0) and (ParCnt = 0) then
          AddTextExpression;
      end
      else if ASplitExpression[I] = AParseFormat.ColumnOpen  then
      begin
        Inc(ColCnt);
        st := st  + ASplitExpression[I];
      end
      else if ASplitExpression[I] = ' ' then
      begin
        if (ParCnt > 0) or (ColCnt > 0) then
          st := st + ASplitExpression[I]
        else if Trim(st) <> '' then
          AddTextExpression;
      end
      else
        st := st + ASplitExpression[I];
    end;

    if (st <> '') then
      AddTextExpression;

    Result := sp;
  finally
    sp := [];
  end;
end;

function SplitExpressionCheckGroup(ASplitExpression: string; AParseFormat: TTMSFNCFilterBuilderParseFormatInfo; var ACheckIsGroup: Boolean): TArray<string>;
var
  I: Integer;
  AndCnt, OrCnt, {XOrCnt,} ExpCnt: Integer;
begin
  Result := [];
  ACheckIsGroup := False;
   
  if Trim(ASplitExpression) <> '' then
  begin
    Result := SplitExpression(ASplitExpression, AParseFormat);

    AndCnt := 0; 
    OrCnt := 0;
//    XOrCnt := 0;
    ExpCnt := 0;

    for I := 0 to Length(Result) - 1 do
    begin
      if Result[I] = AParseFormat.AndOperator then
        Inc(AndCnt)
      else if Result[I] = AParseFormat.OrOperator then
        Inc(OrCnt)
//      else if Result[I] = pf.XOrOperator then
//        Inc(XOrCnt)
      else if Result[I].StartsWith(AParseFormat.ParenthesisOpen) then
        Inc(ExpCnt);
    end;
        
    ACheckIsGroup := (ExpCnt > 0) or (AndCnt > 0) or (OrCnt >0) {or (XOrCnt > 0)};
  end;
end;

function IsGroup(AExpression: string; ABuilder: TTMSFNCCustomFilterBuilder): Boolean;
var
  b: Boolean;
  pf: TTMSFNCFilterBuilderParseFormatInfo;
begin
  if Assigned(ABuilder) then
    pf := GetParseFormatInfo(ABuilder.ParseFormat)
  else
    pf := GetParseFormatInfo;

  SplitExpressionCheckGroup(AExpression, pf, b);
  Result := b;
end;

function RemoveGroupOperators(AGroupExpression: string; AParseFormat: TTMSFNCFilterBuilderParseFormatInfo): string; overload;
begin
  if AGroupExpression.StartsWith(AParseFormat.ParenthesisOpen) and AGroupExpression.EndsWith(AParseFormat.ParenthesisClose) then
  begin
//    {$IFDEF ZEROSTRINGINDEX}
    Result := AGroupExpression.Remove(0, Length(AParseFormat.ParenthesisOpen));
//    {$ELSE}
//    Result := AGroupExpression.Remove(1, Length(AParseFormat.ParenthesisOpen));
//    {$ENDIF}
    Result := Result.Remove(Length(Result) - Length(AParseFormat.ParenthesisClose), Length(AParseFormat.ParenthesisClose));
  end;
end;

function RemoveGroupOperators(AGroupExpression: string; ABuilder: TTMSFNCCustomFilterBuilder): string; overload;
var
  pf: TTMSFNCFilterBuilderParseFormatInfo;
begin
  if Assigned(ABuilder) then
    pf := GetParseFormatInfo(ABuilder.ParseFormat)
  else
    pf := GetDefaultParseFormatInfo;

  Result := RemoveGroupOperators(AGroupExpression, pf);
end;

function ParseValueTextToFNCValue(AText: string; ADataType: TTMSFNCFilterBuilderDataType; AFormatSettings: TFormatSettings): TTMSFNCValue;
var
  dt: TDateTime;
  d: Double;
  i: Int64;
  b, parsed: boolean;
begin
  if ADataType = fdtOther then
  begin
    Result := AText;
    Exit;
  end;

  parsed := False;

  dt := 0;
  d := INFINITE;
  i := INFINITE;
  b := False;

  if ADataType in [fdtDateTime, fdtDate, fdtTime] then
  begin
    parsed := TryStrToDateTime(Trim(AText), dt, AFormatSettings);
    if parsed then
      Result := dt;
  end;

  if ADataType = fdtNumber then
  begin
    parsed := TryStrToInt64(Trim(AText), i);
    if parsed then
      Result := i;
  end;

  if ADataType = fdtFloat then
  begin
    parsed := TryStrToFloat(Trim(AText), d, AFormatSettings);
    if parsed then
      Result := d;
  end;

  if ADataType = fdtBoolean then
  begin
    parsed := TryStrToBool(Trim(AText), b);
    if parsed then
      Result := b;
  end;

  if ADataType = fdtText then
  begin
    Result := AText;
  end
  else if not parsed then
  begin
    Result := TextToFNCValue(AText, AFormatSettings);
  end;
end;

function GetInternalExpressionInformation(AExpression: string; AParseFormat: TTMSFNCFilterBuilderParseFormatInfo; AExpressionOperatorFormat: TTMSFNCFilterBuilderExpressionOperatorFormat; AIsDataSet: Boolean = True): TTMSFNCFilterBuilderExpressionInformation;
var
  op: TTMSFNCFilterBuilderExpressionOperator;
  opTxt: string;
  ExpArr: TArray<string>;
  I, arrStartIdx: Integer;

  function RemoveColumnOperators(AColumnText: string): string;
  begin
    Result := AColumnText;
    if AColumnText.StartsWith(AParseFormat.ColumnOpen) and AColumnText.EndsWith(AParseFormat.ColumnClose) then
    begin
      Result := AColumnText.Remove(0, Length(AParseFormat.ColumnOpen));
      Result := Result.Remove(Length(Result) - Length(AParseFormat.ColumnClose), Length(AParseFormat.ColumnClose));
    end;
  end;

  procedure GetOperatorAndValue(AText: string);
  var
    StrStartIdx: Integer;
    st, upTxt: string;
    iof: TTMSFNCFilterBuilderExpressionOperatorFormat;
  begin
    iof := AExpressionOperatorFormat;
    StrStartIdx := GetStringStartIndex;
    op := feoNotEmpty;
    st := '';
    upTxt := SetUpperCase(AText);

    if upTxt = iof.Empty then
    begin
      op := feoEmpty;
      st := '';
    end
    else if upTxt = iof.NotEmpty then
    begin
      op := feoNotEmpty;
      st := '';
    end
    else if (upTxt.StartsWith(iof.ContainsOpen) or (iof.ContainsOpen = '')) and (upTxt.EndsWith(iof.ContainsClose) or (iof.ContainsClose = '')) and not ((iof.ContainsOpen = '') and (iof.ContainsClose = '')) then
    begin
      op := feoContains;
      st := AText.Remove(StrStartIdx, Length(iof.ContainsOpen));
      st := st.Remove(Length(st) - Length(iof.ContainsClose), Length(iof.ContainsClose));
    end
//    else if (upTxt.StartsWith(iof.NotContainsOpen) or (iof.NotContainsOpen = '')) and (upTxt.EndsWith(iof.NotContainsClose) or (iof.NotContainsClose = '')) then
//    begin
//      op := feoNotContains;
//      st := AText.Remove(StrStartIdx, Length(iof.NotContainsOpen));
//      st := st.Remove(Length(st) - Length(iof.NotContainsClose), Length(iof.NotContainsClose));
//    end
    else if (upTxt.StartsWith(iof.StartsWithOpen) or (iof.StartsWithOpen = '')) and (upTxt.EndsWith(iof.StartsWithClose) or (iof.StartsWithClose = '')) and not ((iof.StartsWithOpen = '') and (iof.StartsWithClose = '')) then
    begin
      op := feoStartsWith;
      st := AText.Remove(StrStartIdx, Length(iof.StartsWithOpen));
      st := st.Remove(Length(st) - Length(iof.StartsWithClose), Length(iof.StartsWithClose));
    end
    else if (upTxt.StartsWith(iof.EndsWithOpen) or (iof.EndsWithOpen = '')) and (upTxt.EndsWith(iof.EndsWithClose) or (iof.EndsWithClose = '')) and not ((iof.EndsWithOpen = '') and (iof.EndsWithClose = '')) then
    begin
      op := feoEndsWith;
      st := AText.Remove(StrStartIdx, Length(iof.EndsWithOpen));
      st := st.Remove(Length(st) - Length(iof.EndsWithClose), Length(iof.EndsWithClose));
    end
    else if (upTxt.StartsWith(iof.NotEqualOpen) or (iof.NotEqualOpen = '')) and (upTxt.EndsWith(iof.NotEqualClose) or (iof.NotEqualClose = '')) and not ((iof.NotEqualOpen = '') and (iof.NotEqualClose = '')) then
    begin
      op := feoNotEqual;
      st := AText.Remove(StrStartIdx, Length(iof.NotEqualOpen));
      st := Trim(st.Remove(Length(st) - Length(iof.NotEqualClose), Length(iof.NotEqualClose)));
    end
    else if (upTxt.StartsWith(iof.LargerThanOrEqualOpen) or (iof.LargerThanOrEqualOpen = '')) and (upTxt.EndsWith(iof.LargerThanOrEqualClose) or (iof.LargerThanOrEqualClose = '')) and not ((iof.LargerThanOrEqualOpen = '') and (iof.LargerThanOrEqualClose = '')) then
    begin
      op := feoLargerThanOrEqual;
      st := AText.Remove(StrStartIdx, Length(iof.LargerThanOrEqualOpen));
      st := Trim(st.Remove(Length(st) - Length(iof.LargerThanOrEqualClose), Length(iof.LargerThanOrEqualClose)));
    end
    else if (upTxt.StartsWith(iof.LargerThanOpen) or (iof.LargerThanOpen = '')) and (upTxt.EndsWith(iof.LargerThanClose) or (iof.LargerThanClose = '')) and not ((iof.LargerThanOpen = '') and (iof.LargerThanClose = '')) then
    begin
      op := feoLargerThan;
      st := AText.Remove(StrStartIdx, Length(iof.LargerThanOpen));
      st := Trim(st.Remove(Length(st) - Length(iof.LargerThanClose), Length(iof.LargerThanClose)));
    end
    else if (upTxt.StartsWith(iof.SmallerThanOrEqualOpen) or (iof.SmallerThanOrEqualOpen = '')) and (upTxt.EndsWith(iof.SmallerThanOrEqualClose) or (iof.SmallerThanOrEqualClose = '')) and not ((iof.SmallerThanOrEqualOpen = '') and (iof.SmallerThanOrEqualClose = '')) then
    begin
      op := feoSmallerThanOrEqual;
      st := AText.Remove(StrStartIdx, Length(iof.SmallerThanOrEqualOpen));
      st := Trim(st.Remove(Length(st) - Length(iof.SmallerThanOrEqualClose), Length(iof.SmallerThanOrEqualClose)));
    end
    else if (upTxt.StartsWith(iof.SmallerThanOpen) or (iof.SmallerThanOpen = '')) and (upTxt.EndsWith(iof.SmallerThanClose) or (iof.SmallerThanClose = '')) and not ((iof.SmallerThanOpen = '') and (iof.SmallerThanClose = '')) then
    begin
      op := feoSmallerThan;
      st := AText.Remove(StrStartIdx, Length(iof.SmallerThanOpen));
      st := Trim(st.Remove(Length(st) - Length(iof.SmallerThanClose), Length(iof.SmallerThanClose)));
    end
    else if (upTxt.StartsWith(iof.EqualStrOpen) or (iof.EqualStrOpen = '')) and (upTxt.EndsWith(iof.EqualStrClose) or (iof.EqualStrClose = '')) and not ((iof.EqualStrOpen = '') and (iof.EqualStrClose = '')) then
    begin
      op := feoEqual;
      st := AText.Remove(StrStartIdx, Length(iof.EqualStrOpen));
      st := st.Remove(Length(st) - Length(iof.EqualStrClose), Length(iof.EqualStrClose));
    end
    else if (upTxt.StartsWith(iof.EqualOpen) or (iof.EqualOpen = '')) and (upTxt.EndsWith(iof.EqualClose) or (iof.EqualClose = '')) and not ((iof.EqualOpen = '') and (iof.EqualClose = '')) then
    begin
      op := feoEqual;
      st := AText.Remove(StrStartIdx, Length(iof.EqualOpen));
      st := Trim(st.Remove(Length(st) - Length(iof.EqualClose), Length(iof.EqualClose)));
    end;

    if Trim(st).StartsWith(iof.StringDelimiter) and Trim(st).EndsWith(iof.StringDelimiter) then
      st := Trim(st);

    Result.ExpressionOperator := op;

    Result.ValueText := st;
  end;
begin
  Result.IsGroup := True;
  Result.Column := nil;
  Result.ColumnName := '';
  Result.ExpressionOperator := feoNotEmpty;
  Result.Value := TValueToFNCValue(TValue.Empty);
  Result.ValueText := '';
  Result.Inverted := False;

  ExpArr := SplitExpressionCheckGroup(AExpression, AParseFormat, Result.IsGroup);

  if Length(ExpArr) = 0 then
  begin
    Result.IsEmpty := True;
    Exit;
  end
  else
    Result.IsEmpty := False;

  if Result.IsGroup or (Length(ExpArr) = 0) then
    Exit;

  if Length(ExpArr) = 1 then
    ExpArr := CustomSplit(AExpression, [AExpressionOperatorFormat.EqualOpen, AExpressionOperatorFormat.ContainsOpen, AExpressionOperatorFormat.EndsWithOpen, AExpressionOperatorFormat.StartsWithOpen, AExpressionOperatorFormat.SmallerThanOpen, AExpressionOperatorFormat.LargerThanOpen], TRUE);

  Result.Inverted := SetUpperCase(ExpArr[0]) = SetUpperCase(AParseFormat.NotOperator);
  if Result.Inverted then
    arrStartIdx := 1
  else
    arrStartIdx := 0;

  for I := arrStartIdx to Length(ExpArr) - 1 do
  begin
    if ((Length(AParseFormat.ColumnOpen) > 0) or (Length(AParseFormat.ColumnOpen) > 0)) and ExpArr[I].StartsWith(AParseFormat.ColumnOpen) and ExpArr[I].EndsWith(AParseFormat.ColumnClose) then
      Result.ColumnName := RemoveColumnOperators(ExpArr[I])
    else if AIsDataSet and (I = arrStartIdx) then
      Result.ColumnName := ExpArr[I]
    else
    begin
      if (opTxt <> '') and not MatchStr(ExpArr[I],[AExpressionOperatorFormat.EqualOpen, AExpressionOperatorFormat.ContainsOpen, AExpressionOperatorFormat.EndsWithOpen, AExpressionOperatorFormat.StartsWithOpen, AExpressionOperatorFormat.SmallerThanOpen, AExpressionOperatorFormat.LargerThanOpen]) then
        opTxt := opTxt + ' ';
      opTxt := opTxt + ExpArr[I];
    end;
  end;

  GetOperatorAndValue(opTxt);
end;

function GetInternalExpressionText(AColumnName: String; ADataType: TTMSFNCFilterBuilderDataType; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValueText: string;
    AValue: TTMSFNCValue; AInverted: Boolean; AFormatSettings: TFormatSettings; AParseFormat: TTMSFNCFilterBuilderParseFormat; AIsDataSet:Boolean): string;
begin
  Result := GetFilterBuilderExpressionText(AColumnName, ADataType, AExpressionOperator, AValueText, AValue, AInverted, AFormatSettings, AParseFormat, AIsDataSet);
end;

//function GroupOperatorFoundForTextFormat(AText: string; AParseFormat: TTMSFNCFilterBuilderParseFormatInfo; var AGroupOperator: TTMSFNCFilterBuilderGroupOperator): Boolean;
//begin
//  Result := True;
//  if SetUpperCase((AText)).Equals(SetUpperCase(AParseFormat.AndOperator)) then
//    AGroupOperator := fgoAND
////  else if SetUpperCase((AText)).Equals(SetUpperCase(AParseFormat.XorOperator)) then
////    AGroupOperator := fgoXOR
//  else if SetUpperCase((AText)).Equals(SetUpperCase(AParseFormat.OrOperator)) then
//    AGroupOperator := fgoOR
//  else
//    Result := False;
//end;

{ TTMSFNCCustomFilterBuilder }

function TTMSFNCCustomFilterBuilder.AddDataColumn: TTMSFNCFilterBuilderColumn;
begin
  Result := FDataColumns.Add;
end;

function TTMSFNCCustomFilterBuilder.AddDataColumn(AName: string; ADataType: TTMSFNCFilterBuilderDataType = fdtAutomatic; ADisplayName: string = ''): TTMSFNCFilterBuilderColumn;
begin
  Result := FDataColumns.AddColumn(AName, ADataType, ADisplayName);
end;

procedure TTMSFNCCustomFilterBuilder.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCCustomFilterBuilder) then
  begin
    FDataColumns.Assign((Source as TTMSFNCCustomFilterBuilder).DataColumns);
    FParseFormat.Assign((Source as TTMSFNCCustomFilterBuilder).ParseFormat);
    FFormatType := (Source as TTMSFNCCustomFilterBuilder).FormatType;
    FFilter.Assign((Source as TTMSFNCCustomFilterBuilder).Filter);
  end
  else
    inherited;
end;

procedure TTMSFNCCustomFilterBuilder.BeginUpdate;
begin
  Inc(FUpdateCount);
end;

function TTMSFNCCustomFilterBuilder.ValidateFilterArray(AInputArray: TTMSFNCFilterValidateInputArray; AByRow: Boolean = False): TTMSFNCFilterValidateOutputArray;
var
  col, row, MaxRowLength: Integer;
  AInput: TTMSFNCFilterValidateInputRow;
begin
  MaxRowLength := 0;

  if AByRow then
  begin
    SetLength(Result, Length(AInputArray));
    for row := 0 to Length(AInputArray) - 1 do
      Result[row] := ValidateFilterRow(AInputArray[row]);
  end
  else
  begin
  //  Check columns are same as builder
    if Length(AInputArray) <> DataColumns.Count then
    begin
      raise Exception.Create('Number of Input Columns not equal to the Data Columns.');  //GVH: Moet event worden
  //    DoValidateDataColumnsCountError(AInputArray, DataColumns);
    end;

    for col := 0 to Length(AInputArray) - 1 do
    begin
      if MaxRowLength < Length(AInputArray[col]) then
          MaxRowLength := Length(AInputArray[col]);
    end;

    SetLength(Result, MaxRowLength);
    SetLength(AInput, DataColumns.Count);

    for row := 0 to MaxRowLength - 1 do
    begin

      for col := 0 to Length(AInputArray) - 1 do
      begin
        if (row < Length(AInputArray[col])) and (col < Length(AInputArray)) then
          AInput[col] := AInputArray[col][row]
        else
          {$IFDEF WEBLIB}
          AInput[col] := nil;
          {$ENDIF}
          {$IFNDEF WEBLIB}
          AInput[col] := TValueToFNCValue(TValue.Empty);
          {$ENDIF}
      end;
      Result[row] := ValidateFilterRow(AInput);
    end;

    SetLength(AInput,0);
  end;
end;

function TTMSFNCCustomFilterBuilder.ValidateFilterRow(AInputData: TTMSFNCFilterValidateInputRow): Boolean;
begin
  Result := FFilter.ValidateFilter(AInputData);
  DoValidateFilter(AInputData, Result);
end;

procedure TTMSFNCCustomFilterBuilder.ClearFilter;
var
  a: Boolean;
begin
  a := True;
  DoBeforeClearFilter(a);

  if a then
  begin
    BeginUpdate;

    FFilter.Groups.Clear;
    FFilter.Expressions.Clear;
    DoInternalFilterChanged(FFilter);

    EndUpdate;

    DoAfterClearFilter;
  end;                                                                              
end;

constructor TTMSFNCCustomFilterBuilder.Create;
begin
  FUpdateCount := 0;

  FParseFormat := TTMSFNCFilterBuilderParseFormat.Create;
  FParseFormat.OnInternalChanged := DoInternalFormatChanged;

  FDataColumns := TTMSFNCFilterBuilderColumns.Create(Self);
  FDataColumns.OnInternalChanged := DoInternalColumnsChanged;
  FDataColumns.OnInternalColumnNameChange := DoOnDataColumnNameChange;
  FDataColumns.OnInternalColumnRemove := DoOnDataColumnRemove;

  FFormatType := fftDelphiDataSet;

  FFilter := TTMSFNCFilterBuilderGroup.Create(Self);
  FFilter.DataString := 'Main Filter';
  FFilter.FBuilder := Self;
  FFilter.OnInternalChanged := DoInternalFilterChanged;
end;

function CreateANDGroup(AGroups: TArray<TTMSFNCFilterBuilderGroup>; AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
begin
  Result := TTMSFNCFilterBuilderGroup.Create;
  Result.InitializeCreate;
  Result.PopulateGroup(AGroups, AExpressions);
end;

function CreateExpression(AColumn: TTMSFNCFilterBuilderColumn; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False): TTMSFNCFilterBuilderExpression;
begin
  Result := TTMSFNCFilterBuilderExpression.Create(nil);
  Result.FColumn := AColumn;
  Result.FExpressionOperator := AExpressionOperator;
  Result.FValueText := ValueToString(AValue);
  Result.FValue := AValue;
end;

function CreateExpression(AColumnName: string; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False): TTMSFNCFilterBuilderExpression;
begin
  Result := TTMSFNCFilterBuilderExpression.Create(nil);
  Result.FBackupColName := AColumnName;
  Result.FExpressionOperator := AExpressionOperator;
  Result.FValueText := ValueToString(AValue);
  Result.FValue := AValue;
  Result.FIsInverted := AInverted;
end;

function CreateORGroup(AGroups: TArray<TTMSFNCFilterBuilderGroup>;  AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
begin
  Result := TTMSFNCFilterBuilderGroup.Create;
  Result.InitializeCreate(fgoOR);
  Result.PopulateGroup(AGroups, AExpressions);
end;

//function CreateXORGroup(AGroups: TArray<TTMSFNCFilterBuilderGroup>; AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
//begin
//  Result := TTMSFNCFilterBuilderGroup.Create;
//  Result.InitializeCreate(fgoXOR);
//  Result.PopulateGroup(AGroups, AExpressions);
//end;

function TTMSFNCCustomFilterBuilder.DeleteExpression(AExpression: TTMSFNCFilterBuilderExpression): Boolean;
var
  fe: TTMSFNCFilterBuilderExpressions;
  idx: Integer;
begin
  Result := False;
  fe := AExpression.FFilterExpressions;
  if Assigned(fe) then
  begin
    idx := fe.IndexOf(AExpression);
    if idx >= 0 then
    begin
      fe.Delete(idx);
      Result := True;
    end;
  end;
end;

function TTMSFNCCustomFilterBuilder.DeleteGroup(AGroup: TTMSFNCFilterBuilderGroup): Boolean;
var
  g: TTMSFNCFilterBuilderGroups;
  idx: Integer;
begin
  Result := False;
  if AGroup = FFilter then
  begin
    ClearFilter;
    Result := True;
  end
  else
  begin
    g := AGroup.FOwnerGroups;
    if Assigned(g) then
    begin
      idx := g.IndexOf(AGroup);
      if idx >= 0 then
      begin
        g.Delete(idx);
        Result := True;
      end;
    end;
  end;
end;

destructor TTMSFNCCustomFilterBuilder.Destroy;
begin
  FFilter.Free;
  FParseFormat.Free;
  FDataColumns.Free;
  inherited;
end;

procedure TTMSFNCCustomFilterBuilder.DoAfterClearFilter;
begin
  if Assigned(FOnAfterClearFilter) then
    FOnAfterClearFilter(Self);
end;

procedure TTMSFNCCustomFilterBuilder.DoBeforeClearFilter(var AAllow: Boolean);
begin
  if Assigned(FOnBeforeClearFilter) then
    FOnBeforeClearFilter(Self, AAllow);
end;

procedure TTMSFNCCustomFilterBuilder.DoChanged;
begin
  if Assigned(FOnChanged) then
    FOnChanged(Self);
end;

procedure TTMSFNCCustomFilterBuilder.DoColumnsChanged;
begin
  if Assigned(FOnColumnsChanged) then
    FOnColumnsChanged(Self);
end;

procedure TTMSFNCCustomFilterBuilder.DoExpressionAdded(AExpression: TTMSFNCFilterBuilderExpression);
begin
  if Assigned(FOnExpressionAdded) then
    FOnExpressionAdded(Self, AExpression, AExpression.Expression);
end;

procedure TTMSFNCCustomFilterBuilder.DoFilterExpressionTextParsed(AExpression: TTMSFNCFilterBuilderExpression; AExpressionText: string);
begin
  if Assigned(FOnExpressionTextParsed) then
    FOnExpressionTextParsed(Self, AExpression,AExpressionText);
end;

procedure TTMSFNCCustomFilterBuilder.DoFilterTextParsed(AFilter: TTMSFNCFilterBuilderGroup; AExpressionText: string);
begin
  if Assigned(FOnFilterTextParsed) then
    FOnFilterTextParsed(Self, AFilter, AExpressionText);
end;

procedure TTMSFNCCustomFilterBuilder.DoGetDisplayFilterText(AFilter: TTMSFNCFilterBuilderGroup; var AText: string);
begin
  if Assigned(FOnGetDisplayFilterText) then
    FOnGetDisplayFilterText(Self, AFilter, AText);
end;

procedure TTMSFNCCustomFilterBuilder.DoGetExpressionText(AExpression: TTMSFNCFilterBuilderExpression; var AText: string);
begin
  if Assigned(FOnGetExpressionText) then
    FOnGetExpressionText(Self, AExpression, AText);
end;

procedure TTMSFNCCustomFilterBuilder.DoGetFilterText(AFilter: TTMSFNCFilterBuilderGroup; var AText: string);
begin
  if Assigned(FOnGetFilterText) then
    FOnGetFilterText(Self, AFilter, AText);
end;

procedure TTMSFNCCustomFilterBuilder.DoGetFormatSettings(var AFormatSettings: TFormatSettings);
begin
  if Assigned(FOnGetFormatSettings) then
    FOnGetFormatSettings(Self, AFormatSettings);
end;

procedure TTMSFNCCustomFilterBuilder.DoGetGroupText(AGroup: TTMSFNCFilterBuilderGroup; var AText: string);
begin
  if Assigned(FOnGetGroupText) then
    FOnGetGroupText(Self, AGroup, AText);
end;

procedure TTMSFNCCustomFilterBuilder.DoInternalChanged;
begin
  if Assigned(FOnInternalChanged) then
    FOnInternalChanged(Self);

  if FUpdateCount = 0 then
    DoChanged;
end;

procedure TTMSFNCCustomFilterBuilder.DoInternalColumnsChanged(Sender: TObject);
begin
  DoInternalChanged;

  if FUpdateCount = 0 then
  begin
    DoColumnsChanged;
  end;
end;

procedure TTMSFNCCustomFilterBuilder.DoInternalFilterChanged(Sender: TObject);
begin
//  if FUpdateCount = 0 then
    DoInternalChanged;
end;

procedure TTMSFNCCustomFilterBuilder.DoInternalFormatChanged(Sender: TObject);
begin
  if FUpdateCount = 0 then
    DoParseFormatChanged;
end;

procedure TTMSFNCCustomFilterBuilder.DoOnExpressionAdded(AExpression: TTMSFNCFilterBuilderExpression);
begin
  DoInternalFilterChanged(Filter);
  DoExpressionAdded(AExpression);
end;

procedure TTMSFNCCustomFilterBuilder.DoParseError(AFilterText: string);
begin
  if Assigned(OnParseError) then
    OnParseError(Self, AFilterText);
end;

procedure TTMSFNCCustomFilterBuilder.DoParseErrorInvalidExpression(AFilterText: string; AErrorPosition: Integer);
begin
  if Assigned(OnParseErrorInvalidExpression) then
    OnParseErrorInvalidExpression(Self, AFilterText, AErrorPosition);
end;

procedure TTMSFNCCustomFilterBuilder.DoParseErrorOperatorMismatch(AFilterText: string; AErrorPosition: Integer);
begin
  if Assigned(OnParseErrorOperatorMismatch) then
    OnParseErrorOperatorMismatch(Self, AFilterText, AErrorPosition);
end;

procedure TTMSFNCCustomFilterBuilder.DoParseErrorOperatorPosition(AFilterText: string; AErrorPosition: Integer);
begin
  if Assigned(OnParseErrorOperatorPosition) then
    OnParseErrorOperatorPosition(Self, AFilterText, AErrorPosition);
end;

procedure TTMSFNCCustomFilterBuilder.DoParseErrorParenthesis(AFilterText: string; AErrorPosition: Integer);
begin
  if Assigned(OnParseErrorParenthesis) then
    OnParseErrorParenthesis(Self, AFilterText, AErrorPosition);
end;

procedure TTMSFNCCustomFilterBuilder.DoParseFormatChanged;
begin
  if Assigned(FOnParseFormatChanged) then
    FOnParseFormatChanged(Self);
end;

procedure TTMSFNCCustomFilterBuilder.DoValidateExpression(AExpression: TTMSFNCFilterBuilderExpression; AInputValue: TTMSFNCValue; var AValidationResult, AExitValidation: Boolean);
begin
  if Assigned(FOnValidateExpression) then
    FOnValidateExpression(Self, AExpression, AInputValue, AValidationResult, AExitValidation);
end;

procedure TTMSFNCCustomFilterBuilder.DoValidateFilter(AInputData: TArray<TTMSFNCValue>; var AValidationResult: Boolean);
begin
  if Assigned(FOnValidateFilter) then
    FOnValidateFilter(Self, AInputData, AValidationResult);
end;

procedure TTMSFNCCustomFilterBuilder.DoValidateGroup(AGroup: TTMSFNCFilterBuilderGroup; AInputData: TTMSFNCFilterValidateInputRow; var AValidationResult, AExitValidation: Boolean);
begin
  if Assigned(FOnValidateGroup) then
    FOnValidateGroup(Self, AGroup, AInputData, AValidationResult, AExitValidation);
end;

procedure TTMSFNCCustomFilterBuilder.EndUpdate;
begin
  inherited;
  Dec(FUpdateCount);

  if FUpdateCount <= 0 then
  begin
//    DoInternalChanged;
    FUpdateCount := 0;
  end;
end;

function TTMSFNCCustomFilterBuilder.ParseExpression(AFilterText: string): Boolean;
begin
  BeginUpdate;
  ClearFilter;
  Result := isExpressionValid(AFilterText);
  if Result then
  begin
    if (AFilterText <> '') then
      FFilter.ParseExpression(AFilterText);

    DoFilterTextParsed(FFilter, AFilterText);
  end;
  EndUpdate;
end;

function TTMSFNCCustomFilterBuilder.GetColumnByDisplayName(AColumnDisplayName: string): TTMSFNCFilterBuilderColumn;
var
  I: Integer;
begin
  Result := nil;
  for I := 0 to DataColumns.Count - 1 do
  begin
    if SetUpperCase(DataColumns[I].DisplayName) = SetUpperCase(AColumnDisplayName) then
    begin
      Result := DataColumns[I];
      Break;
    end;
  end;
end;

function TTMSFNCCustomFilterBuilder.GetColumnByName(AColumnName: string): TTMSFNCFilterBuilderColumn;
var
  I: Integer;
begin
  Result := nil;
  for I := 0 to DataColumns.Count - 1 do
  begin
    if SetUpperCase(DataColumns[I].Name) = SetUpperCase(AColumnName) then
    begin
      Result := DataColumns[I];
      Break;
    end
    else if SetUpperCase(DataColumns[I].DisplayName) = SetUpperCase(AColumnName) then
      Result := DataColumns[I];
  end;
end;

function TTMSFNCCustomFilterBuilder.GetDataColumn(AName: string): TTMSFNCFilterBuilderColumn;
begin
  Result := GetColumnByName(AName);
  if not Assigned(Result) then
    Result := AddDataColumn(AName);
end;

function TTMSFNCCustomFilterBuilder.GetDataColumnDisplayName(AName: string): string;
var
  c: TTMSFNCFilterBuilderColumn;
begin
  Result := '';
  c := DataColumn[AName];
  if Assigned(c) then
  begin
    Result := c.DisplayName;
    if Result = '' then
      Result := c.Name;
  end;
end;

function TTMSFNCCustomFilterBuilder.GetDataColumnType(AName: string): TTMSFNCFilterBuilderDataType;
var
  c: TTMSFNCFilterBuilderColumn;
begin
  Result := fdtAutomatic;
  c := DataColumn[AName];
  if Assigned(c) then
    Result := c.DataType;
end;

function TTMSFNCCustomFilterBuilder.GetDisplayFilterText: string;
begin
  Result := Filter.GetInternalFilterText(True);
end;

function TTMSFNCCustomFilterBuilder.GetFilterText: string;
begin
  Result := Filter.FilterText;
end;

function TTMSFNCCustomFilterBuilder.GetFormatSettings: TFormatSettings;
var
  fs: TFormatSettings;
begin
  fs := TFormatSettings.Create;
  DoGetFormatSettings(fs);
  Result := fs;
end;

function TTMSFNCCustomFilterBuilder.GetIsEmpty: Boolean;
begin
  if Assigned(Filter) then
    Result := (Filter.Groups.Count = 0) and (Filter.Expressions.Count = 0)
  else
    Result := False;
end;

function TTMSFNCCustomFilterBuilder.GetExpressionInformation(AExpression: string): TTMSFNCFilterBuilderExpressionInformation;
var
  fs: TFormatSettings;
  pf: TTMSFNCFilterBuilderParseFormatInfo;
  eof: TTMSFNCFilterBuilderExpressionOperatorFormat;
  dt: TTMSFNCFilterBuilderDataType;
  st: string;
  StrStartIdx: Integer;
begin
  pf := GetParseFormatInfo(FParseFormat);
  eof := GetExpressionOperatorFormat;

  Result := GetInternalExpressionInformation(AExpression, pf, eof, FormatType = fftDelphiDataSet);

  if Result.ColumnName <> '' then
  begin
    Result.Column := GetColumnByName(Result.ColumnName);
    if not Assigned(Result.Column) then
      Result.Column := GetColumnByDisplayName(Result.ColumnName);
  end;

  if Assigned(Result.Column) then
    dt := Result.Column.DataType
  else
    dt := fdtAutomatic;

  StrStartIdx := GetStringStartIndex;

  if ((dt = fdtText) or ((FormatType = fftDelphiDataSet) and (dt in [fdtDateTime, fdtDate, fdtTime]))) and Trim(Result.ValueText).StartsWith(eof.StringDelimiter) and Trim(Result.ValueText).EndsWith(eof.StringDelimiter) then
  begin
    st := Trim(Result.ValueText).Remove(StrStartIdx, Length(eof.StringDelimiter));
    Result.ValueText := st.Remove(Length(st) - Length(eof.StringDelimiter), Length(eof.StringDelimiter));
  end;

  fs := GetFormatSettings;
  Result.Value := ParseValueTextToFNCValue(Result.ValueText, dt, fs);
end;

function TTMSFNCCustomFilterBuilder.GetExpressionOperatorFormat: TTMSFNCFilterBuilderExpressionOperatorFormat;
var
  spl: TArray<string>;
  splTxt: string;
begin
  Result.MultiCharWildCard := Trim(ParseFormat.MultiCharWildCard);
  Result.SingleCharWildCard := Trim(ParseFormat.SingleCharWildCard);
  Result.StringDelimiter := Trim(ParseFormat.StringDelimiter);

  splTxt := SetUpperCase(ParseFormat.StartsWithOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.StartsWithOpen := Trim(spl[0])
  else
    Result.StartsWithOpen := '';

  if Length(spl) > 1 then
    Result.StartsWithClose := Trim(spl[1])
  else
    Result.StartsWithClose := '';

  splTxt := SetUpperCase(ParseFormat.EndsWithOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.EndsWithOpen := Trim(spl[0])
  else
    Result.EndsWithOpen := '';

  if Length(spl) > 1 then
    Result.EndsWithClose := Trim(spl[1])
  else
    Result.EndsWithClose := '';

  splTxt := SetUpperCase(ParseFormat.ContainsOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.ContainsOpen := Trim(spl[0])
  else
    Result.ContainsOpen := '';

  if Length(spl) > 1 then
    Result.ContainsClose := Trim(spl[1])
  else
    Result.ContainsClose := '';

//  splTxt := SetUpperCase(ParseFormat.NotContainsOperator);
//  spl := splTxt.Split(['#VALUE']);
//  Result.NotContainsOpen := Trim(spl[0]);
//  if Length(spl) > 1 then
//    Result.NotContainsClose := Trim(spl[1])
//  else
//    Result.NotContainsClose := '';


  splTxt := SetUpperCase(ParseFormat.EqualOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.EqualOpen := Trim(spl[0])
  else
    Result.EqualOpen := '';

  if Length(spl) > 1 then
    Result.EqualClose := Trim(spl[1])
  else
    Result.EqualClose := '';

  splTxt := SetUpperCase(ParseFormat.EqualStrOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.EqualStrOpen := Trim(spl[0])
  else
    Result.EqualStrOpen := '';

  if Length(spl) > 1 then
    Result.EqualStrClose := Trim(spl[1])
  else
    Result.EqualStrClose := '';

  splTxt := SetUpperCase(ParseFormat.NotEqualOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.NotEqualOpen := Trim(spl[0])
  else
    Result.NotEqualOpen := '';

  if Length(spl) > 1 then
    Result.NotEqualClose := Trim(spl[1])
  else
    Result.NotEqualClose := '';

  splTxt := SetUpperCase(Trim(ParseFormat.EmptyOperator));
  Result.Empty := splTxt;
  splTxt := SetUpperCase(Trim(ParseFormat.notEmptyOperator));
  Result.NotEmpty := splTxt;

  splTxt := SetUpperCase(ParseFormat.LargerThanOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.LargerThanOpen := Trim(spl[0])
  else
    Result.LargerThanOpen := '';

  if Length(spl) > 1 then
    Result.LargerThanClose := Trim(spl[1])
  else
    Result.LargerThanClose := '';

  splTxt := SetUpperCase(ParseFormat.SmallerThanOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.SmallerThanOpen := Trim(spl[0])
  else
    Result.SmallerThanOpen := '';

  if Length(spl) > 1 then
    Result.SmallerThanClose := Trim(spl[1])
  else
    Result.SmallerThanClose := '';

  splTxt := SetUpperCase(ParseFormat.LargerThanOrEqualOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.LargerThanOrEqualOpen := Trim(spl[0])
  else
    Result.LargerThanOrEqualOpen := '';

  if Length(spl) > 1 then
    Result.LargerThanOrEqualClose := Trim(spl[1])
  else
    Result.LargerThanOrEqualClose := '';

  splTxt := SetUpperCase(ParseFormat.SmallerThanOrEqualOperator);
  spl := splTxt.Split(['#VALUE']);
  if Length(spl) > 0 then
    Result.SmallerThanOrEqualOpen := Trim(spl[0])
  else
    Result.SmallerThanOrEqualOpen := '';

  if Length(spl) > 1 then
    Result.SmallerThanOrEqualClose := Trim(spl[1])
  else
    Result.SmallerThanOrEqualClose := '';
end;

function TTMSFNCCustomFilterBuilder.GetExpressionText(AColumnName: String; ADataType: TTMSFNCFilterBuilderDataType; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValueText: string; AInverted: Boolean): string;
begin
  Result := GetInternalExpressionText(AColumnName, ADataType, AExpressionOperator, AValueText, TValueToFNCValue(TValue.Empty), AInverted, GetFormatSettings, ParseFormat, FormatType = fftDelphiDataSet);
end;

function TTMSFNCCustomFilterBuilder.GetExpressionText(AColumnName: String; ADataType: TTMSFNCFilterBuilderDataType;
  AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValueText: string; AValue: TTMSFNCValue; AInverted: Boolean): string;
begin
  Result := GetInternalExpressionText(AColumnName, ADataType, AExpressionOperator, AValueText, AValue, AInverted, GetFormatSettings, ParseFormat, FormatType = fftDelphiDataSet);
end;

function TTMSFNCCustomFilterBuilder.GetOperatorText(AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator): string;
begin
  case AExpressionOperator of
    feoStartsWith: Result := Trim(FParseFormat.StartsWithOperator);
    feoEndsWith: Result := Trim(FParseFormat.EndsWithOperator);
    feoContains: Result := Trim(FParseFormat.ContainsOperator);
//    feoNotContains: Result := Trim(FParseFormat.NotContainsOperator);
    feoEqual: Result := Trim(FParseFormat.EqualOperator);
    feoNotEqual: Result := Trim(FParseFormat.NotEqualOperator);
    feoEmpty: Result := Trim(FParseFormat.EmptyOperator);
    feoNotEmpty: Result := Trim(FParseFormat.NotEmptyOperator);
    feoLargerThan: Result := Trim(FParseFormat.LargerThanOperator);
    feoSmallerThan: Result := Trim(FParseFormat.SmallerThanOperator);
    feoLargerThanOrEqual: Result := Trim(FParseFormat.LargerThanOrEqualOperator);
    feoSmallerThanOrEqual: Result := Trim(FParseFormat.SmallerThanOrEqualOperator);
  end;                                             
end;

procedure TTMSFNCCustomFilterBuilder.HandleValidationErrors(AText: string; ACheckInfo: TTMSFNCFilterBuilderExpressionCheckInfo);
begin
  if not ACheckInfo.IsValid then
  begin
    if ACheckInfo.ErrorParenthesis >= 0 then
      DoParseErrorParenthesis(AText, ACheckInfo.ErrorParenthesis);
    if ACheckInfo.ErrorOperatorPosition >= 0 then
      DoParseErrorOperatorPosition(AText, ACheckInfo.ErrorOperatorPosition);
    if ACheckInfo.ErrorOperatorMismatch >= 0 then
      DoParseErrorOperatorMismatch(AText, ACheckInfo.ErrorOperatorMismatch);
    if ACheckInfo.ErrorInvalidExpression >= 0 then
      DoParseErrorInvalidExpression(AText, ACheckInfo.ErrorInvalidExpression);

//    if Assigned(InterfacedObject) and Supports(FInterfacedObject, ITMSFNCFilter, io) then
//      io.ParseError(AText);

    DoParseError(AText);  
  end;
end;

function TTMSFNCCustomFilterBuilder.InternalCheckExpression(AExpression: string): TTMSFNCFilterBuilderExpressionCheckInfo;
var
  pfi: TTMSFNCFilterBuilderParseFormatInfo;
  ExpArr: TArray<string>;
  part, prevPart, original: string;
  I, parCnt, colCnt: Integer;
  newGroup, firstOperator: Boolean;
  operatorStack: TStack<TTMSFNCFilterBuilderGroupOperator>;
  gop: TTMSFNCFilterBuilderGroupOperator;

  function IsOperator(const Token: string): Boolean;
  begin
    Result := SetUpperCase(Token).Equals(SetUpperCase(ParseFormat.AndOperator))
              or SetUpperCase(Token).Equals(SetUpperCase(ParseFormat.OrOperator));
//              or SetUpperCase(Token).Equals(SetUpperCase(ParseFormat.XorOperator));
  end;

  function GetOperator(const Token: string): TTMSFNCFilterBuilderGroupOperator;
  begin
    if SetUpperCase(Token).Equals(SetUpperCase(ParseFormat.AndOperator)) then
      Result := fgoAND
//    else if SetUpperCase(Token).Equals(SetUpperCase(ParseFormat.XorOperator)) then
//      Result := fgoXOR
    else
      Result := fgoOR;
  end;
begin
  Result.IsValid := True;
  Result.ErrorParenthesis := -1;
  Result.ErrorOperatorPosition := -1;
  Result.ErrorOperatorMismatch := -1;
  Result.ErrorInvalidExpression := -1;

  if Trim(AExpression) = '' then
    Exit;

  pfi := GetParseFormatInfo;
  part := '';
  prevPart := '';
  original := '';

  operatorStack := TStack<TTMSFNCFilterBuilderGroupOperator>.Create;

  newGroup := False;
  firstOperator := True;
  parCnt := 0;    
  colCnt := 0;

  try
    ExpArr := CustomSplit(AExpression, [pfi.ParenthesisOpen, pfi.ParenthesisClose, pfi.ColumnOpen, pfi.ColumnClose,' '], True);

    for I := 0 to Length(ExpArr) - 1 do
    begin
      if Trim(ExpArr[I]) <> '' then
      begin
        part := Trim(ExpArr[I]);
        if part.StartsWith(pfi.ParenthesisOpen) and (colCnt = 0) then
        begin
          Inc(parCnt);
          newGroup := True;
        end
        else if (part = pfi.ParenthesisClose) and (colCnt = 0) then
        begin
          if IsOperator(prevPart) then      //Can't close group after operator
          begin
            Result.IsValid := False;
            Result.ErrorOperatorPosition := Length(original);

            Exit;
          end;

          Dec(parCnt);

          if (parCnt < 0) then          //More closing then opening parentheses
          begin
            Result.IsValid := False;
            Result.ErrorParenthesis := Length(original);

            Exit;
          end
          else if (operatorStack.Count > 1) then               
            operatorStack.Pop; //Get to previous group operator
        end
        else if part = pfi.ColumnOpen then
          Inc(colCnt)
        else if part = pfi.ColumnClose then
          Dec(colCnt)
        else if IsOperator(part) then
        begin
          gop := GetOperator(part);
          if newGroup then                                   //If open parenthesis passed
          begin
            operatorStack.Push(gop);
            newGroup := False;
          end
          else if firstOperator then                         //If it is the first operator of main group
          begin
            operatorStack.Push(gop);
            firstOperator := False;
          end
          else if gop <> operatorStack.Peek then            //Check for different operators in same group
          begin
            Result.IsValid := False;
            Result.ErrorOperatorMismatch := Length(original);

            Exit;
          end;
        end;

        prevPart := part;
      end;
      original := original + ExpArr[I];
    end;
  finally
    operatorStack.Free;
  end;

  if IsOperator(prevPart) then  //Expression can't end on operator
  begin
    Result.IsValid := False;
    Result.ErrorParenthesis := Length(original);

    Exit;
  end;

  if parCnt <> 0 then  //Combination of parentheses not correct
  begin
    Result.IsValid := False;
    Result.ErrorOperatorPosition := Length(original);

    Exit;
  end;

  if colCnt > 0 then //Combination of column indicators not correct
  begin
    Result.IsValid := False;
    Result.ErrorInvalidExpression := Length(original);

    Exit;
  end;
end;

function TTMSFNCCustomFilterBuilder.isExpressionValid(AExpression: string): Boolean;
var
  gi: TTMSFNCFilterBuilderExpressionCheckInfo;
begin
  gi := InternalCheckExpression(AExpression);
  Result := gi.IsValid;
  HandleValidationErrors(AExpression, gi);
end;

procedure TTMSFNCCustomFilterBuilder.DoOnDataColumnNameChange(Sender: TObject; AColumn: TTMSFNCFilterBuilderColumn);
  procedure ChangeNameExpressionCol(AExpression: TTMSFNCFilterBuilderExpression);
  begin
    if Assigned(AExpression.Column) and (AExpression.Column = AColumn) then
      AExpression.FBackupColName := AColumn.DisplayName;
  end;
  procedure ChangeNameGroupExprCol(AGroup: TTMSFNCFilterBuilderGroup);
  var
    I: Integer;
  begin
    for I := 0 to AGroup.Groups.Count - 1 do
      ChangeNameGroupExprCol(AGroup.Groups[I]);
    for I := 0 to AGroup.Expressions.Count - 1 do
      ChangeNameExpressionCol(AGroup.Expressions[I]);
  end;
begin
  ChangeNameGroupExprCol(FFilter);
end;

procedure TTMSFNCCustomFilterBuilder.DoOnDataColumnRemove(Sender: TObject; AColumn: TTMSFNCFilterBuilderColumn);
  procedure RemoveExpressionCol(AExpression: TTMSFNCFilterBuilderExpression);
  begin
    if Assigned(AExpression.Column) and (AExpression.Column = AColumn) then
      AExpression.Column := nil;
  end;
  procedure RemoveGroupExprCol(AGroup: TTMSFNCFilterBuilderGroup);
  var
    I: Integer;
  begin
    for I := 0 to AGroup.Groups.Count - 1 do
      RemoveGroupExprCol(AGroup.Groups[I]);
    for I := 0 to AGroup.Expressions.Count - 1 do
      RemoveExpressionCol(AGroup.Expressions[I]);
  end;
begin
  RemoveGroupExprCol(FFilter);
end;

function TTMSFNCCustomFilterBuilder.GetOperatorText(AGroupOperator: TTMSFNCFilterBuilderGroupOperator): string;
begin
  Result := GetGroupOperatorText(AGroupOperator, FParseFormat);
end;

function TTMSFNCCustomFilterBuilder.PopulateFilter(AGroups: TArray<TTMSFNCFilterBuilderGroup>; AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
begin
  FFilter.PopulateGroup(AGroups, AExpressions);
  Result := FFilter;
end;

function TTMSFNCCustomFilterBuilder.PopulateFilterExpressions(AExpressions: TArray<TTMSFNCFilterBuilderExpression>): TTMSFNCFilterBuilderGroup;
begin
  Result := PopulateFilter([], AExpressions);
end;

function TTMSFNCCustomFilterBuilder.PopulateFilterGroups(AGroups: TArray<TTMSFNCFilterBuilderGroup>): TTMSFNCFilterBuilderGroup;
begin
  Result := PopulateFilter(AGroups, []);
end;

procedure TTMSFNCCustomFilterBuilder.SetDataColumnDisplayName(AName: string; const Value: string);
var
  c: TTMSFNCFilterBuilderColumn;
begin
  c := DataColumn[AName];
  if Assigned(c) then
    c.DisplayName := Value;
end;

procedure TTMSFNCCustomFilterBuilder.SetDataColumns(const Value: TTMSFNCFilterBuilderColumns);
begin
  FDataColumns.Assign(Value);
  DoInternalColumnsChanged(FDataColumns);
end;

procedure TTMSFNCCustomFilterBuilder.SetDataColumnType(AName: string; const Value: TTMSFNCFilterBuilderDataType);
var
  c: TTMSFNCFilterBuilderColumn;
begin
  c := DataColumn[AName];
  if Assigned(c) then
    c.DataType := Value;
end;

procedure TTMSFNCCustomFilterBuilder.SetDelphiDataSetFormat(AStringDelimiter: Char = ''''; ASingleWildCard: Char = '_'; AMultiCharWildCard: Char = '%');
begin
  SetFilterFormatDelphiDataSet(FParseFormat, AStringDelimiter, AStringDelimiter, AMultiCharWildCard);
end;

procedure TTMSFNCCustomFilterBuilder.SetDisplayFilterText(const Value: string);
begin
  ParseExpression(Value);
end;

procedure TTMSFNCCustomFilterBuilder.SetFilter(const Value: TTMSFNCFilterBuilderGroup);
begin
  FFilter.Assign(Value);
  DoInternalFilterChanged(FFilter);
end;

procedure TTMSFNCCustomFilterBuilder.SetFilterText(const Value: string);
begin
  ParseExpression(Value);
end;

procedure TTMSFNCCustomFilterBuilder.SetUniversalFilterExpressionsFormat(AStringDelimiter: Char = '"'; ASingleWildCard: Char = '?'; AMultiCharWildCard: Char = '*');
begin
  SetFilterFormatUniversalFilterExpressions(FParseFormat, AStringDelimiter, ASingleWildCard, AMultiCharWildCard);
end;

procedure TTMSFNCCustomFilterBuilder.SetFormatType(const Value: TTMSFNCFilterBuilderFormatType);
begin
  FFormatType := Value;

  case FFormatType of
    fftDelphiDataSet: SetDelphiDataSetFormat;
    fftUniversalFilterExpressions: SetUniversalFilterExpressionsFormat;
  end;
end;

procedure TTMSFNCCustomFilterBuilder.SetParseFormat(const Value: TTMSFNCFilterBuilderParseFormat);
begin
  FParseFormat.Assign(Value);
  DoInternalFormatChanged(FParseFormat);
end;

{ TTMSFNCFilterBuilderColumns }

function TTMSFNCFilterBuilderColumns.Add(AColumnClass: TTMSFNCFilterBuilderColumnClass = nil): TTMSFNCFilterBuilderColumn;
var
  c: TTMSFNCFilterBuilderColumn;
begin
  if Assigned(AColumnClass) then
    c := AColumnClass.Create(Self)
  else
    c := TTMSFNCFilterBuilderColumn.Create(Self);

  inherited Add(c);
  Result := c;
end;

function TTMSFNCFilterBuilderColumns.AddColumn(AName: string; ADataType: TTMSFNCFilterBuilderDataType = fdtAutomatic; ADisplayName: string = ''): TTMSFNCFilterBuilderColumn;
begin
  Result := Add;
  Result.FName := AName;
  Result.FDataType := ADataType;
  Result.FDisplayName := ADisplayName;
end;

procedure TTMSFNCFilterBuilderColumns.Assign(Source: TTMSFNCFilterBuilderColumns);
var
  I: Integer;
  c: TTMSFNCFilterBuilderColumn;
begin
  if (Source is TTMSFNCFilterBuilderColumns) then
  begin
    Clear;

    for I := 0 to (Source as TTMSFNCFilterBuilderColumns).Count - 1 do
    begin
      c := Add;
      c.Assign((Source as TTMSFNCFilterBuilderColumns).Items[I]);
    end;
  end
  else
    inherited;
end;

constructor TTMSFNCFilterBuilderColumns.Create(AOwner: TPersistent);
begin
  inherited Create;
//  FOwner := AOwner;
end;

procedure TTMSFNCFilterBuilderColumns.DoChanged;
begin
  if Assigned(FOnInternalChanged) then
    FOnInternalChanged(Self);
end;

procedure TTMSFNCFilterBuilderColumns.DoColumnNameChange(AColumn: TTMSFNCFilterBuilderColumn);
begin
  if Assigned(FOnInternalColumnNameChange) then
    FOnInternalColumnNameChange(Self, AColumn);
end;

procedure TTMSFNCFilterBuilderColumns.DoColumnRemove(AColumn: TTMSFNCFilterBuilderColumn);
begin
  if Assigned(FOnInternalColumnRemove) then
    FOnInternalColumnRemove(Self, AColumn);
end;

function TTMSFNCFilterBuilderColumns.GetColumnIndex(AColumn: TTMSFNCFilterBuilderColumn): Integer;
var
  I: Integer;
begin
  Result := -1;
  for I := 0 to Count - 1 do
  begin
    if AColumn = Items[I] then
    begin
      Result := I;
      Break;
    end;
  end;
end;

{ TTMSFNCFilterBuilderColumn }

procedure TTMSFNCFilterBuilderColumn.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCFilterBuilderColumn) then
  begin
    FName := (Source as TTMSFNCFilterBuilderColumn).Name;
    FDisplayName := (Source as TTMSFNCFilterBuilderColumn).DisplayName;
    FDataType := (Source as TTMSFNCFilterBuilderColumn).DataType;
  end
  else
    inherited;
end;

constructor TTMSFNCFilterBuilderColumn.Create(AColumns: TTMSFNCFilterBuilderColumns);
begin
  FColumns := AColumns;
  FName := 'Name' + GetIndex.ToString;
  FDisplayName := '';
  FDataType := fdtAutomatic;
end;

destructor TTMSFNCFilterBuilderColumn.Destroy;
begin
  DoRemoveColumn;
  inherited;
end;

procedure TTMSFNCFilterBuilderColumn.DoChanged;
begin
  if Assigned(FColumns)then
    FColumns.DoChanged;
end;

procedure TTMSFNCFilterBuilderColumn.DoColumnNameChange;
begin
  if Assigned(FColumns) then
   FColumns.DoColumnNameChange(Self);
end;

procedure TTMSFNCFilterBuilderColumn.DoListChanged(Sender: TObject);
begin
  DoChanged;
end;

procedure TTMSFNCFilterBuilderColumn.DoRemoveColumn;
begin
  if Assigned(FColumns) then
   FColumns.DoColumnRemove(Self);
end;

function TTMSFNCFilterBuilderColumn.GetDisplayName: string;
begin
  if FDisplayName <> '' then
    Result := FDisplayName
  else
    Result := FName;
end;

function TTMSFNCFilterBuilderColumn.GetIndex: Integer;
begin
  Result := -1;
  if Assigned(FColumns) then
    Result := FColumns.GetColumnIndex(Self);
end;

procedure TTMSFNCFilterBuilderColumn.SetDataType(const Value: TTMSFNCFilterBuilderDataType);
begin
  if FDataType <> Value then
  begin
    FDataType := Value;
    DoChanged;
  end;
end;

procedure TTMSFNCFilterBuilderColumn.SetDisplayName(const Value: string);
begin
  if FDisplayName <> Value then
  begin
    FDisplayName := Value;
//    DoColumnNameChange;
    DoChanged;
  end;
end;

procedure TTMSFNCFilterBuilderColumn.SetName(const Value: string);
begin
  if FName <> Value then
  begin
    FName := Value;
//    DoColumnNameChange;
    DoChanged;
  end;
end;

{ TTMSFNCFilterBuilderGroup }

function TTMSFNCFilterBuilderGroup.AddExpression(AColumnName: string; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator;
  AValue: TTMSFNCValue; AInverted: Boolean = False; ACaseSensitive: Boolean = False): TTMSFNCFilterBuilderExpression;
begin
  Result := Expressions.Add(AColumnName, AExpressionOperator, AValue, AInverted, ACaseSensitive);
end;

function TTMSFNCFilterBuilderGroup.AddExpression(AColumn: TTMSFNCFilterBuilderColumn; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator;
  AValue: TTMSFNCValue; AInverted: Boolean = False; ACaseSensitive: Boolean = False): TTMSFNCFilterBuilderExpression;
begin
  Result := Expressions.Add(AColumn, AExpressionOperator, AValue, AInverted, ACaseSensitive);
end;

function TTMSFNCFilterBuilderGroup.AddExpression(AColumnName: string; AValue: TTMSFNCValue): TTMSFNCFilterBuilderExpression;
begin
  Result := Expressions.Add(AColumnName, AValue);
end;

function TTMSFNCFilterBuilderGroup.AddANDGroup: TTMSFNCFilterBuilderGroup;
begin
  Result := AddGroup(fgoAND);
end;

function TTMSFNCFilterBuilderGroup.AddExpression( AColumn: TTMSFNCFilterBuilderColumn;  AValue: TTMSFNCValue): TTMSFNCFilterBuilderExpression;
begin
  Result := Expressions.Add(AColumn, AValue);
end;

function TTMSFNCFilterBuilderGroup.AddGroup(
  AGroupOperator: TTMSFNCFilterBuilderGroupOperator): TTMSFNCFilterBuilderGroup;
begin
  Result := Groups.Add;
  Result.FGroupOperator := AGroupOperator;
end;

function TTMSFNCFilterBuilderGroup.AddORGroup: TTMSFNCFilterBuilderGroup;
begin
  Result := AddGroup(fgoOR);
end;

function TTMSFNCFilterBuilderGroup.AddExpression: TTMSFNCFilterBuilderExpression;
begin
  Result := Expressions.Add;
end;

procedure TTMSFNCFilterBuilderGroup.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCFilterBuilderGroup) then
  begin
    FGroupOperator := (Source as TTMSFNCFilterBuilderGroup).GroupOperator;
    FGroups.Assign((Source as TTMSFNCFilterBuilderGroup).Groups);
    FExpressions.Assign((Source as TTMSFNCFilterBuilderGroup).FExpressions);
  end
  else
    inherited;
end;

procedure TTMSFNCFilterBuilderGroup.BeginUpdate;
begin
  Inc(FUpdateCount);
end;

procedure TTMSFNCFilterBuilderGroup.ChangeExpressionToGroup(AExpressionText: String; AExpression: TTMSFNCFilterBuilderExpression);
begin
  RemoveExpression(AExpression);

  if AExpressionText <> '' then
  begin
    ParseExpression(RemoveGroupOperators(AExpressionText, GetBuilder));
  end;
end;

procedure TTMSFNCFilterBuilderGroup.Clear;
var
  b: TTMSFNCCustomFilterBuilder;
begin
  b := FBuilder;
  if Assigned(b) and (Self = b.Filter) then
    b.ClearFilter
  else
  begin
    FGroups.Clear;
    FExpressions.Clear;
  end;
end;

constructor TTMSFNCFilterBuilderGroup.Create(ABuilder: TTMSFNCCustomFilterBuilder);
begin
  FBuilder := ABuilder;
  FOwnerGroups := nil;

  InitializeCreate;
end;

constructor TTMSFNCFilterBuilderGroup.Create(AGroups: TTMSFNCFilterBuilderGroups);
begin
  FBuilder := nil;
  FOwnerGroups := AGroups;

  InitializeCreate;
end;

destructor TTMSFNCFilterBuilderGroup.Destroy;
begin
  FGroups.Free;
  FExpressions.Free;
  inherited;
end;

procedure TTMSFNCFilterBuilderGroup.DoChanged;
begin
  if Assigned(FOwnerGroups) then
    FOwnerGroups.DoChanged;

  if Assigned(FOnInternalChanged) then
    FOnInternalChanged(Self);
end;

procedure TTMSFNCFilterBuilderGroup.DoGetFilterBuilder(var AFilterBuilder: TTMSFNCCustomFilterBuilder);
begin
  if Assigned(OnGetFilterBuilder) then
    OnGetFilterBuilder(Self, AFilterBuilder);
end;

procedure TTMSFNCFilterBuilderGroup.DoInternalGroupsChanged(Sender: TObject);
begin
  DoChanged;
end;

procedure TTMSFNCFilterBuilderGroup.DoInternalExpressionsChanged(Sender: TObject);
begin
  DoChanged;
end;

procedure TTMSFNCFilterBuilderGroup.EndUpdate;
begin
  Dec(FUpdateCount);

  if FUpdateCount <= 0 then
  begin
    FUpdateCount := 0;
  end;
end;

procedure TTMSFNCFilterBuilderGroup.ParseExpression(AExpression: string);
var
  b: TTMSFNCCustomFilterBuilder;
  ExpArr: TArray<string>;
  gftext, iftext: string;
  J: Integer;
  OrOp{, XOrOp}: boolean;
  pfi: TTMSFNCFilterBuilderParseFormatInfo;

  function IsOperator(const Token: string): Boolean;
  begin
    Result := SetUpperCase(Token).Equals(SetUpperCase(pfi.AndOperator))
              or SetUpperCase(Token).Equals(SetUpperCase(pfi.OrOperator));
//              or SetUpperCase(Token).Equals(SetUpperCase(pfi.XorOperator));
  end;
begin
  if AExpression = '' then
  begin
    RemoveThisGroup;
    Exit;
  end;

  BeginUpdate;

  FGroups.Clear;
  FExpressions.Clear;

  if Trim(AExpression) <> '' then
  begin
    b := GetBuilder;
    if Assigned(b) then
      pfi := GetParseFormatInfo(b.ParseFormat)
    else
      pfi := GetDefaultParseFormatInfo;

    OrOp := False;
//    XOrOp := False;

    //Split array in parts
    ExpArr := SplitExpression(AExpression, pfi);

    if (Length(ExpArr) = 1) and ExpArr[0].StartsWith(pfi.ParenthesisOpen) and ExpArr[0].EndsWith(pfi.ParenthesisClose) then
      ExpArr := SplitExpression(RemoveGroupOperators(AExpression, pfi), pfi);

    iftext := '';

    for J := 0 to Length(ExpArr) - 1 do
    begin
      if ExpArr[J].StartsWith(pfi.ParenthesisOpen) and ExpArr[J].EndsWith(pfi.ParenthesisClose) then
      begin
        if iftext <> '' then
        begin
          FExpressions.Add(Trim(iftext));
          iftext := '';
        end;
        gftext := RemoveGroupOperators(ExpArr[J], b);
        FGroups.Add(gftext);
      end
      else if IsOperator(ExpArr[J]) then
      begin
        if iftext <> '' then
        begin
          FExpressions.Add(Trim(iftext));
          iftext := '';
        end;

        OrOp := SetUpperCase(ExpArr[J]) = SetUpperCase(pfi.OrOperator);
//        XOrOp := SetUpperCase(ExpArr[J]) = SetUpperCase(pfi.XOrOperator);
      end
      else if Trim(ExpArr[J]) <> '' then
        iftext := iftext + ' ' + Trim(ExpArr[J]);
    end;

    if iftext <> '' then
      FExpressions.Add(Trim(iftext));

    if OrOp then
      FGroupOperator := fgoOR
//    else if XOrOp then
//      FGroupOperator := fgoXOR
    else
      FGroupOperator := fgoAND;

    EndUpdate;
  end;
end;

procedure TTMSFNCFilterBuilderGroup.PopulateGroup(AGroups: TArray<TTMSFNCFilterBuilderGroup>; AExpressions: TArray<TTMSFNCFilterBuilderExpression>);
var
  I: Integer;
begin
  FGroups.Clear;
  FExpressions.Clear;
  for I := 0 to Length(AGroups) - 1 do
  begin
    FGroups.Add(AGroups[I]);
    AGroups[I].FOwnerGroups := FGroups;
  end;

  for I := 0 to Length(AExpressions) - 1 do
  begin
    FExpressions.Add(AExpressions[I]);
    AExpressions[I].FFilterExpressions := FExpressions;
  end;
end;

function TTMSFNCFilterBuilderGroup.GetBuilder: TTMSFNCCustomFilterBuilder;
begin
  Result := FBuilder;
  if Assigned(FOwnerGroups) and Assigned(FOwnerGroups.FParentGroup) then
    Result := FOwnerGroups.FParentGroup.GetBuilder;

  DoGetFilterBuilder(Result);
end;

function TTMSFNCFilterBuilderGroup.GetFilterText: string;
begin
  Result := GetInternalFilterText(False);
end;

function TTMSFNCFilterBuilderGroup.GetInternalFilterText(ADisplayNames: Boolean= False): string;
var
  t, o: string;
  I: Integer;
  b: TTMSFNCCustomFilterBuilder;
  pf: TTMSFNCFilterBuilderParseFormatInfo;
  exp, gr: string;
begin

  b := GetBuilder;

  if Assigned(b) then
    pf := GetParseFormatInfo(b.ParseFormat)
  else
    pf := GetDefaultParseFormatInfo;

  case FGroupOperator of
    fgoAND: o := pf.AndOperator;
//    fgoXOR: o := pf.XOrOperator;
    else
      o := pf.OrOperator;
  end;

  t := '';

  for I := 0 to FGroups.Count - 1 do
  begin
    gr := FGroups[I].GetInternalFilterText(True);
    if (I > 0) and (gr <> '') then
      t := t + ' ' + o + ' ';
    t := t + pf.ParenthesisOpen + gr + pf.ParenthesisClose;
  end;

  for I := 0 to FExpressions.Count - 1 do
  begin
    exp := FExpressions[I].GetInternalExpression(ADisplayNames);
    if (t <> '') and (exp <> '') then
      t := t + ' ' + o + ' ';
    t := t + exp;
  end;

  if Assigned(b) then
  begin
    if Self = b.Filter then
    begin
      if ADisplayNames then
        b.DoGetDisplayFilterText(b.Filter, t)
      else
        b.DoGetFilterText(b.Filter, t)
    end
    else
      b.DoGetGroupText(Self, t);
  end;

  Result := t;
end;

function TTMSFNCFilterBuilderGroup.GetGroupOperatorText: string;
var
  b: TTMSFNCCustomFilterBuilder;
begin
  Result := '';
  b := GetBuilder;
  if Assigned(b) then
    Result := b.GetOperatorText(FGroupOperator);
end;

function TTMSFNCFilterBuilderGroup.GetIndex: Integer;
begin
  Result := -1;
  if Assigned(FGroups) then
    Result := FGroups.GetGroupIndex(Self);
end;

procedure TTMSFNCFilterBuilderGroup.InitializeCreate(AGroupOperator: TTMSFNCFilterBuilderGroupOperator = fgoAND);
begin
  FGroupOperator := AGroupOperator;
  FGroups := TTMSFNCFilterBuilderGroups.Create(Self);
  FGroups.OnInternalChanged := DoInternalGroupsChanged;
  FExpressions := TTMSFNCFilterBuilderExpressions.Create(Self);
  FExpressions.OnInternalChanged := DoInternalExpressionsChanged;
end;

procedure TTMSFNCFilterBuilderGroup.RemoveGroup(AGroup: TTMSFNCFilterBuilderGroup);
begin
  FGroups.Delete(AGroup.GetIndex);
end;

procedure TTMSFNCFilterBuilderGroup.RemoveExpression(AExpression: TTMSFNCFilterBuilderExpression);
begin
  FExpressions.Delete(AExpression.GetIndex);
end;

procedure TTMSFNCFilterBuilderGroup.RemoveThisGroup;
begin
  if Assigned(FGroups) and Assigned(FGroups.FParentGroup) then
    FGroups.FParentGroup.RemoveGroup(Self);
end;

procedure TTMSFNCFilterBuilderGroup.SetGroups(const AValue: TTMSFNCFilterBuilderGroups);
begin
  FGroups.Assign(AValue);
  DoChanged;
end;

procedure TTMSFNCFilterBuilderGroup.SetExpressions(const AValue: TTMSFNCFilterBuilderExpressions);
begin
  FExpressions.Assign(AValue);
  DoChanged;
end;

function TTMSFNCFilterBuilderGroup.ValidateFilter(AInputData: TArray<TTMSFNCValue>): Boolean;
var
  ex, isValid, expres: Boolean;
  b: TTMSFNCCustomFilterBuilder;
  colIdx: Integer;
  I{, xorCnt}: Integer;
  val: TTMSFNCValue;
  dt: TTMSFNCFilterBuilderDataType;

  function ExitValidation(AIsValid: Boolean): Boolean;
  begin
    Result := False;
    isValid := AIsValid;
    case GroupOperator of
      fgoAND: Result := not AIsValid;
      fgoOR: Result := AIsValid;
//      fgoXOR:
//      begin
//        if AIsValid then
//          Inc(xorCnt);
//      end;
    end;
  end;
begin
//  xorCnt := 0;
  isValid := True;

  b := GetBuilder;

  for I := 0 to FGroups.Count - 1 do
  begin
    expRes := FGroups[I].ValidateFilter(AInputData);
    ex := ExitValidation(expRes);

    if Assigned(b) then
      b.DoValidateGroup(FGroups[I], AInputData, expRes, ex);

    if ex then
    begin
      Result := isValid;
      Exit;
    end;
  end;

  for I := 0 to FExpressions.Count - 1 do
  begin
    colIdx := -1;
    dt := fdtAutomatic;
    if Assigned(b) then
    begin
      FExpressions[I].UpdateColumn;

      val := TValueToFNCValue(TValue.Empty);

      if Assigned(FExpressions[I].Column) then
      begin
        colIdx := FExpressions[I].Column.GetIndex;
        if colIdx < b.DataColumns.Count then
          val := AInputData[colIdx];
        dt := FExpressions[I].Column.DataType;
      end;
    end;

    if colIdx >= 0 then
    begin
      expRes := FExpressions[I].InternalValidateExpression(val, dt);
      ex := ExitValidation(expRes);

      if Assigned(b) then
        b.DoValidateExpression(FExpressions[I], val, expRes, ex);
    end
    else
      ex := ExitValidation(False);

    if ex then
    begin
      Result := isValid;
      Exit;
    end;
  end;
  //Result is XOR Odd, AND true, OR False --> But isValid would have exited after test if it was other value
  Result := ({(FGroupOperator = fgoXOR) and Odd(xorCnt)) or ((FGroupOperator <> fgoXOR) and} isValid);
end;

procedure TTMSFNCFilterBuilderGroup.SetGroupOperator(const AValue: TTMSFNCFilterBuilderGroupOperator);
begin
  if FGroupOperator <> AValue then
  begin
    FGroupOperator := AValue;
    DoChanged;
  end;
end;

{ TTMSFNCFilterBuilderGroups }

function TTMSFNCFilterBuilderGroups.Add: TTMSFNCFilterBuilderGroup;
var
  g: TTMSFNCFilterBuilderGroup;
begin
  g := TTMSFNCFilterBuilderGroup.Create(Self);
  g.FOwnerGroups := Self;
  inherited Add(g);
  Result := g;
end;

function TTMSFNCFilterBuilderGroups.Add(AGroup: TTMSFNCFilterBuilderGroup): TTMSFNCFilterBuilderGroup;
begin
  AGroup.FOwnerGroups := Self;
  inherited Add(AGroup);
  Result := AGroup;
end;

procedure TTMSFNCFilterBuilderGroups.Assign(Source: TTMSFNCFilterBuilderGroups);
var
  I: Integer;
  g: TTMSFNCFilterBuilderGroup;
begin
  if (Source is TTMSFNCFilterBuilderGroups) then
  begin
    Clear;

    for I := 0 to (Source as TTMSFNCFilterBuilderGroups).Count - 1 do
    begin
      g := Add;
      g.Assign((Source as TTMSFNCFilterBuilderGroups).Items[I]);
    end;
  end
  else
    inherited;
end;

function TTMSFNCFilterBuilderGroups.Add(AExpression: string): TTMSFNCFilterBuilderGroup;
begin
  Result := Add;
  if AExpression <> '' then
    Result.ParseExpression(AExpression);
end;

constructor TTMSFNCFilterBuilderGroups.Create(AOwner: TPersistent);
begin
  inherited Create;

  FBuilder := nil;
  FParentGroup := nil;

  if AOwner is TTMSFNCCustomFilterBuilder then
    FBuilder := TTMSFNCCustomFilterBuilder(AOwner)
  else if AOwner is TTMSFNCFilterBuilderGroup then
    FParentGroup := TTMSFNCFilterBuilderGroup(AOwner);
end;

destructor TTMSFNCFilterBuilderGroups.Destroy;
begin
  inherited;
end;

procedure TTMSFNCFilterBuilderGroups.DoChanged;
begin
  if Assigned(FOnInternalChanged) then
    FOnInternalChanged(Self);
end;

procedure TTMSFNCFilterBuilderGroups.DoInternalGroupsChanged(Sender: TObject);
begin
  DoChanged;
end;

procedure TTMSFNCFilterBuilderGroups.DoInternalExpressionsChanged(Sender: TObject);
begin
  DoChanged;
end;

function TTMSFNCFilterBuilderGroups.GetBuilder: TTMSFNCCustomFilterBuilder;
begin
  Result := FBuilder;
  if Assigned(FParentGroup) then
    Result := FParentGroup.GetBuilder;
end;

function TTMSFNCFilterBuilderGroups.GetGroupIndex(AGroup: TTMSFNCFilterBuilderGroup): Integer;
var
  I: Integer;
begin
  Result := -1;
  for I := 0 to Count - 1 do
  begin
    if AGroup = Items[I] then
    begin
      Result := I;
      Break;
    end;
  end;
end;

{ TTMSFNCFilterBuilderExpression }

procedure TTMSFNCFilterBuilderExpression.Assign(Source: TPersistent);
var
  b: TTMSFNCCustomFilterBuilder;
begin
  if (Source is TTMSFNCFilterBuilderExpression) then
  begin
    b := GetBuilder;
    if Assigned(b) and Assigned((Source as TTMSFNCFilterBuilderExpression).Column) then
      FColumn := b.GetColumnByName((Source as TTMSFNCFilterBuilderExpression).Column.Name);

    if not Assigned(FColumn) then
      FBackupColName := (Source as TTMSFNCFilterBuilderExpression).FBackupColName;

    FValue := (Source as TTMSFNCFilterBuilderExpression).Value;
    FValueText := (Source as TTMSFNCFilterBuilderExpression).FValueText;
    FIsInverted := (Source as TTMSFNCFilterBuilderExpression).IsInverted;
    FExpressionOperator := (Source as TTMSFNCFilterBuilderExpression).ExpressionOperator;
    FCaseSensitive := (Source as TTMSFNCFilterBuilderExpression).CaseSensitive;
  end
  else
    inherited;
end;

procedure TTMSFNCFilterBuilderExpression.CheckExpression;
begin
  FValidExpression := (Assigned(FColumn) or (FBackupColName <> '')) and not ValueIsEmpty(Value);
end;

constructor TTMSFNCFilterBuilderExpression.Create(AFilterExpressions: TTMSFNCFilterBuilderExpressions);
begin
  inherited Create;
  FFilterExpressions := AFilterExpressions;
  FColumn := nil;
  FValue := '';
  FValueText := '';
  FIsInverted := False;
  FCaseSensitive := False;
  FExpressionOperator := feoNotEmpty;
end;

destructor TTMSFNCFilterBuilderExpression.Destroy;
begin
  inherited;
end;

procedure TTMSFNCFilterBuilderExpression.DoChanged;
begin
  if Assigned(FFilterExpressions) then
    FFilterExpressions.DoChanged;
end;

procedure TTMSFNCFilterBuilderExpression.DoGetFilterBuilder(var AFilterBuilder: TTMSFNCCustomFilterBuilder);
begin
  if Assigned(OnGetFilterBuilder) then
    OnGetFilterBuilder(Self, AFilterBuilder);
end;

procedure TTMSFNCFilterBuilderExpression.UpdateColumn;
var
  b: TTMSFNCCustomFilterBuilder;
begin
  if not Assigned(FColumn) then
  begin
    b := GetBuilder;
    if Assigned(b) and (FBackupColName <> '') then
      FColumn := b.GetColumnByName(FBackupColName);
  end;
end;

function TTMSFNCFilterBuilderExpression.GetBuilder: TTMSFNCCustomFilterBuilder;
begin
  Result := nil;
  if Assigned(FFilterExpressions) and Assigned(FFilterExpressions.FParentGroup) then
    Result := FFilterExpressions.FParentGroup.GetBuilder;

  DoGetFilterBuilder(Result);
end;

function TTMSFNCFilterBuilderExpression.GetColumnDisplayName: string;
begin
  if Assigned(FColumn) then
    Result := FColumn.DisplayName
  else
    Result := FBackupColName;
end;

function TTMSFNCFilterBuilderExpression.GetColumnName: string;
begin
  if Assigned(FColumn) then
    Result := FColumn.Name
  else
    Result := FBackupColName;
end;

function TTMSFNCFilterBuilderExpression.GetExpression: string;
begin
  Result := GetInternalExpression(False);
end;

function TTMSFNCFilterBuilderExpression.GetInternalExpression(ADisplayNames: Boolean = False): string;
var
  b: TTMSFNCCustomFilterBuilder;
  dt: TTMSFNCFilterBuilderDataType;
  fs: TFormatSettings;
  pf: TTMSFNCFilterBuilderParseFormat;
  colName, exp: string;
begin
  Result := '';

  b := GetBuilder;

  if not Assigned(Column) and (FBackupColName <> '') then
    UpdateColumn;

  if Assigned(FColumn) then
  begin
    if ADisplayNames then
      colName := FColumn.DisplayName
    else
      colName := FColumn.Name;
    dt := FColumn.DataType;
  end
  else
  begin
    colName := FBackupColName;
    dt := fdtAutomatic;
  end;

  if Assigned(b) then
    exp := b.GetExpressionText(colName, dt, FExpressionOperator, FValueText, FValue, IsInverted)
  else
  begin
    fs := TFormatSettings.Create;
    pf := TTMSFNCFilterBuilderParseFormat.Create;
    try
      exp := GetInternalExpressionText(colName, dt, FExpressionOperator, FValueText, FValue, IsInverted, fs, pf, True);
    finally
      pf.Free;
    end;
  end;

  if Assigned(b) then
    b.DoGetExpressionText(Self, exp);

  Result := exp;
end;

function TTMSFNCFilterBuilderExpression.GetIndex: Integer;
begin
  Result := -1;
  if Assigned(FFilterExpressions) then
    Result := FFilterExpressions.GetExpressionIndex(Self);
end;

function TTMSFNCFilterBuilderExpression.GetParentGroup: TTMSFNCFilterBuilderGroup;
begin
  Result := nil;
  if Assigned(FFilterExpressions) and Assigned(FFilterExpressions.FParentGroup) then
    Result := FFilterExpressions.FParentGroup;
end;

function TTMSFNCFilterBuilderExpression.GetValueDataType: TTMSFNCFilterBuilderDataType;
begin
  Result := fdtAutomatic;
  UpdateColumn;

  if Assigned(FColumn) then
    Result := FColumn.FDataType;
end;

function TTMSFNCFilterBuilderExpression.InternalValidateExpression(AInput: TTMSFNCValue; ADataType: TTMSFNCFilterBuilderDataType): Boolean;
var
  b: TTMSFNCCustomFilterBuilder;
  eof: TTMSFNCFilterBuilderExpressionOperatorFormat;
  inp, val: TTMSFNCValue;
  inpTxt,valTxt: string;
  fs: TFormatSettings;
  isText: boolean;
  StrStartIdx: Integer;
  dt: TDateTime;
  f: Double;
  i: Integer;
begin
  Result := False;

  StrStartIdx := GetStringStartIndex;

  b := GetBuilder;
  if Assigned(b) then
  begin
    fs := b.GetFormatSettings;
    eof := b.GetExpressionOperatorFormat;
  end
  else
  begin
    fs := TFormatSettings.Create;
    eof := GetDefaultExpressionOperatorFormat;
  end;

  inp := AInput;
  inpTxt := ValueToString(AInput);
  val := FValue;

  if (ADataType in [fdtText]) or (FExpressionOperator in [feoContains, {feoNotContains, }feoStartsWith, feoEndsWith]) then
  begin
    if FValueText <> '' then
      valTxt := FValueText
    else
      valTxt := ValueToString(val);
  end
  else if (ADataType in [fdtDateTime, fdtDate, fdtTime]) and ValueIsDateTime(AInput) then
  begin
    inpTxt := DateTimeToStr(ValueToDateTime(AInput), fs);
    if not ValueIsDateTimeType(inp) then
    begin
      dt := INFINITE;
      TryStrToDateTime(inpTxt, dt);
      if dt <> INFINITE then
        inp := dt;
    end;

    valTxt := DateTimeToStr(ValueToDateTime(FValue), fs);
  end
  else if (ADataType = fdtFloat) then
  begin
    if not ValueIsFloat(inp) then
    begin
      f := INFINITE;
      TryStrToFloat(inpTxt, f, fs);
      if f <> INFINITE then
        inp := f;
    end;
  end
  else if (ADataType = fdtNumber) then
  begin
    if not ValueIsInteger(inp) then
    begin
      i := MaxInt;
      TryStrToInt(inpTxt, i);
      if i <> MaxInt then
        inp := i;
    end;
  end
  else if (ADataType = fdtAutomatic) then
  begin
    if ValueIsString(AInput) then
      inp :=  ParseValueTextToFNCValue(ValueToString(AInput), ADataType, fs);

    if ValueIsDateTime(AInput) then
      inpTxt := DateTimeToStr(ValueToDateTime(AInput), fs);
    if ValueIsString(FValue) then
      val := ParseValueTextToFNCValue(ValueToString(FValue), ADataType, fs);

    if FValueText <> '' then
      valTxt := FValueText
    else
      valTxt := ValueToString(val);
  end;

  isText := (ADataType = fdtText) or
            ((ADataType = fdtAutomatic) and ValueIsString(inp)) or
            ((ADataType in [fdtDateTime, fdtDate, fdtTime]) and (ContainsText(valTxt, eof.SingleCharWildCard) or ContainsText(valTxt, eof.MultiCharWildCard))) or
            (valTxt.StartsWith(eof.StringDelimiter) and valTxt.EndsWith(eof.StringDelimiter));

  if valTxt.StartsWith(eof.StringDelimiter) and valTxt.EndsWith(eof.StringDelimiter) then
  begin
    valTxt := valTxt.Remove(StrStartIdx, Length(eof.StringDelimiter));
    valTxt := valTxt.Remove(Length(valTxt) - Length(eof.StringDelimiter), Length(eof.StringDelimiter));
  end;

  if not FCaseSensitive then
  begin
    inpTxt := UpperCase(inpTxt);
    valTxt := UpperCase(valTxt);
  end;

  case FExpressionOperator of
    feoStartsWith:
    begin
//      if isText and (ContainsText(valTxt, eof.SingleCharWildCard) or ContainsText(valTxt, eof.MultiCharWildCard)) then
        Result := MatchStrings(valTxt + eof.MultiCharWildCard, inpTxt, eof.MultiCharWildCard, eof.SingleCharWildCard)
//      else
//        Result := inpTxt.StartsWith(valTxt);
    end;
    feoEndsWith:
    begin
//      if isText and (ContainsText(valTxt, eof.SingleCharWildCard) or ContainsText(valTxt, eof.MultiCharWildCard)) then
        Result := MatchStrings(eof.MultiCharWildCard + valTxt, inpTxt, eof.MultiCharWildCard, eof.SingleCharWildCard)
//      else
//        Result := inpTxt.EndsWith(valTxt);
    end;
    feoContains:
    begin
//      if isText and (ContainsText(valTxt, eof.SingleCharWildCard) or ContainsText(valTxt, eof.MultiCharWildCard)) then
        Result := MatchStrings(eof.MultiCharWildCard + valTxt + eof.MultiCharWildCard, inpTxt, eof.MultiCharWildCard, eof.SingleCharWildCard)
//      else
//        Result := Pos(valTxt, inpTxt) > 0; //GVH: ANSIpos?
    end;
    feoEqual:
    begin
      if isText and (ContainsText(valTxt, eof.SingleCharWildCard) or ContainsText(valTxt, eof.MultiCharWildCard)) then
        Result := MatchStrings(valTxt, inpTxt, eof.MultiCharWildCard, eof.SingleCharWildCard)
      else
        Result := IsFNCValueEqual(inp, val, fs);
    end;
    feoNotEqual:
    begin
      if isText and (ContainsText(valTxt, eof.SingleCharWildCard) or ContainsText(valTxt, eof.MultiCharWildCard)) then
        Result := not MatchStrings(valTxt, inpTxt, eof.MultiCharWildCard, eof.SingleCharWildCard)
      else
        Result := not IsFNCValueEqual(inp, val, fs);
    end;
    feoEmpty: Result := ValueIsEmpty(inp);
    feoNotEmpty: Result := not ValueIsEmpty(inp);
    feoLargerThan:
    begin
//      if isText then
//        Result := CompareStr(inpTxt, valTxt) > 0
//      else
        Result := CompareFNCValue(inp, val, fs) > 0;
    end;
    feoSmallerThan: Result :=  CompareFNCValue(inp, val, fs) < 0;
    feoLargerThanOrEqual: Result := CompareFNCValue(inp, val, fs) >= 0;
    feoSmallerThanOrEqual: Result := CompareFNCValue(inp, val, fs) <= 0;
  end;

  if FIsInverted then
    Result := not Result;
end;

procedure TTMSFNCFilterBuilderExpression.RemoveExpression;
begin
  if Assigned(FFilterExpressions) and Assigned(FFilterExpressions.FParentGroup) then
    FFilterExpressions.FParentGroup.RemoveExpression(Self);
end;

procedure TTMSFNCFilterBuilderExpression.GenerateExpression(AExpression: string; ADataSet: Boolean);
var
  b: TTMSFNCCustomFilterBuilder;
  ei: TTMSFNCFilterBuilderExpressionInformation;
begin
  if (AExpression = '') then
  begin
    RemoveExpression;            //GVH              validate expression?
    Exit;
  end;

  b := GetBuilder;

  if Assigned(b) then
    ei := b.GetExpressionInformation(AExpression)
  else
  begin
    ei := GetInternalExpressionInformation(AExpression, GetDefaultParseFormatInfo(ADataSet),  GetDefaultExpressionOperatorFormat(ADataSet), ADataSet);
    ei.Value := ParseValueTextToFNCValue(ei.ValueText, fdtAutomatic, TFormatSettings.Create);
  end;

  if ei.IsGroup then
  begin
    if Assigned(ParentGroup) then
      ParentGroup.ChangeExpressionToGroup(AExpression, Self);   //GVH
    Exit;
  end;

  FValueText := ei.ValueText;
  FValue := ei.Value;
  FBackupColName := ei.ColumnName;
  FColumn := ei.Column;
  FExpressionOperator := ei.ExpressionOperator;
  FIsInverted := ei.Inverted;

  //------------------------------

  if Assigned(b) then
    b.DoFilterExpressionTextParsed(Self, AExpression);
end;

procedure TTMSFNCFilterBuilderExpression.SetCaseSensitive(const Value: Boolean);
begin
  if FCaseSensitive <> Value then
  begin
    FCaseSensitive := Value;
    DoChanged;
  end;
end;

procedure TTMSFNCFilterBuilderExpression.SetColumn(const AValue: TTMSFNCFilterBuilderColumn);
begin
  if FColumn <> AValue then
  begin
    FColumn := AValue;
    DoChanged;
  end;
end;

procedure TTMSFNCFilterBuilderExpression.SetColumnByName(AName: string);
var
  b: TTMSFNCCustomFilterBuilder;
begin
  b := GetBuilder;
  if Assigned(b) then
    FColumn := b.GetColumnByName(AName);

  FBackupColName := AName;

  DoChanged;
end;

procedure TTMSFNCFilterBuilderExpression.SetExpressionOperator(const AValue: TTMSFNCFilterBuilderExpressionOperator);
begin
  if FExpressionOperator <> AValue then
  begin
    FExpressionOperator := AValue;
    DoChanged;
  end;
end;

procedure TTMSFNCFilterBuilderExpression.SetInverted(const AValue: Boolean);
begin
  if FIsInverted <> AValue then
  begin
    FIsInverted := AValue;
    DoChanged;
  end;
end;

procedure TTMSFNCFilterBuilderExpression.SetValue(const AValue: TTMSFNCValue);
begin
  FValueText := ValueToString(AValue);
  FValue := AValue;
  DoChanged;
end;

function TTMSFNCFilterBuilderExpression.ValidateExpression(AInput: TTMSFNCValue): Boolean;
begin
  Result := InternalValidateExpression(AInput, fdtAutomatic);
end;

{ TTMSFNCFilterBuilderExpressions }

function TTMSFNCFilterBuilderExpressions.Add: TTMSFNCFilterBuilderExpression;
var
  i: TTMSFNCFilterBuilderExpression;
begin
  i := TTMSFNCFilterBuilderExpression.Create(Self);
  inherited Add(i);
  Result := i;
end;

function TTMSFNCFilterBuilderExpressions.Add(AColumn: TTMSFNCFilterBuilderColumn; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator; AValue: TTMSFNCValue; AInverted: Boolean = False; ACaseSensitive: Boolean = False): TTMSFNCFilterBuilderExpression;
begin
  Result := Add;
  Result.FColumn := AColumn;
  Result.FBackupColName := AColumn.GetDisplayName;
  Result.FExpressionOperator := AExpressionOperator;
  Result.FValueText := ValueToString(AValue);
  Result.FValue := AValue;
  Result.FIsInverted := AInverted;
  Result.FCaseSensitive := ACaseSensitive;
  DoExpressionAdded(Result);
end;

function TTMSFNCFilterBuilderExpressions.Add(AExpression: string): TTMSFNCFilterBuilderExpression;
begin
  Result := Add;
  if (AExpression <> '') and not IsGroup(AExpression, GetBuilder) then
    Result.GenerateExpression(AExpression);
  DoExpressionAdded(Result);
end;

function TTMSFNCFilterBuilderExpressions.Add(AColumn: TTMSFNCFilterBuilderColumn; AValue: TTMSFNCValue): TTMSFNCFilterBuilderExpression;
begin
  Result := Add(AColumn, feoEqual, AValue);
end;

function TTMSFNCFilterBuilderExpressions.Add(AColumnName: string; AValue: TTMSFNCValue): TTMSFNCFilterBuilderExpression;
begin
  Result := Add(AColumnName, feoEqual, AValue);
end;

function TTMSFNCFilterBuilderExpressions.Add(AColumnName: string; AExpressionOperator: TTMSFNCFilterBuilderExpressionOperator;
  AValue: TTMSFNCValue; AInverted: Boolean = False; ACaseSensitive: Boolean = False): TTMSFNCFilterBuilderExpression;
var
  b: TTMSFNCCustomFilterBuilder;
  c: TTMSFNCFilterBuilderColumn;
begin
  b := Builder;
  c := nil;
  if Assigned(b) then
    c := b.DataColumn[AColumnName];

  Result := Add(c, AExpressionOperator, AValue, AInverted, ACaseSensitive);
end;

function TTMSFNCFilterBuilderExpressions.Add(AExpression: TTMSFNCFilterBuilderExpression): TTMSFNCFilterBuilderExpression;
begin
  inherited Add(AExpression);
  Result := AExpression;
end;

procedure TTMSFNCFilterBuilderExpressions.Assign(Source: TTMSFNCFilterBuilderExpressions);
var
  I: Integer;
  it: TTMSFNCFilterBuilderExpression;
begin
  if (Source is TTMSFNCFilterBuilderExpressions) then
  begin
    Clear;

    for I := 0 to (Source as TTMSFNCFilterBuilderExpressions).Count - 1 do
    begin
      it := Add;
      it.Assign((Source as TTMSFNCFilterBuilderExpressions).Items[I]);
    end;
  end
  else
    inherited;
end;

constructor TTMSFNCFilterBuilderExpressions.Create(AOwner: TPersistent);
begin
  inherited Create;
  FParentGroup := nil;
  if AOwner is TTMSFNCFilterBuilderGroup then
    FParentGroup := TTMSFNCFilterBuilderGroup(AOwner);
end;

procedure TTMSFNCFilterBuilderExpressions.DoChanged;
begin
  if Assigned(FOnInternalChanged) then
    FOnInternalChanged(Self);
end;

procedure TTMSFNCFilterBuilderExpressions.DoExpressionAdded(AExpression: TTMSFNCFilterBuilderExpression);
var
  b: TTMSFNCCustomFilterBuilder;
begin
  b := GetBuilder;
  if Assigned(b) then
    b.DoOnExpressionAdded(AExpression);
end;

function TTMSFNCFilterBuilderExpressions.GetBuilder: TTMSFNCCustomFilterBuilder;
begin
  Result := nil;
  if Assigned(FParentGroup) then
    Result := FParentGroup.GetBuilder;
end;

function TTMSFNCFilterBuilderExpressions.GetExpressionIndex(AExpression: TTMSFNCFilterBuilderExpression): Integer;
var
  I: Integer;
begin
  Result := -1;
  for I := 0 to Count - 1 do
  begin
    if AExpression = Items[I] then
    begin
      Result := I;
      Break;
    end;
  end;
end;

{ TTMSFNCFilterBuilderParseFormat }

procedure TTMSFNCFilterBuilderParseFormat.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCFilterBuilderParseFormat) then
  begin
    FAndOperator := (Source as TTMSFNCFilterBuilderParseFormat).AndOperator;
    FColumnFormat := (Source as TTMSFNCFilterBuilderParseFormat).ColumnFormat;
    FContainsOperator := (Source as TTMSFNCFilterBuilderParseFormat).ContainsOperator;
    FEmptyOperator := (Source as TTMSFNCFilterBuilderParseFormat).EmptyOperator;
    FEndsWithOperator := (Source as TTMSFNCFilterBuilderParseFormat).EndsWithOperator;
    FEqualOperator := (Source as TTMSFNCFilterBuilderParseFormat).EqualOperator;
    FLargerThanOrEqualOperator := (Source as TTMSFNCFilterBuilderParseFormat).LargerThanOrEqualOperator;
    FLargerThanOperator := (Source as TTMSFNCFilterBuilderParseFormat).LargerThanOperator;
    FGroupFormat := (Source as TTMSFNCFilterBuilderParseFormat).GroupFormat;
    FSmallerThanOrEqualOperator := (Source as TTMSFNCFilterBuilderParseFormat).SmallerThanOrEqualOperator;
    FSmallerThanOperator := (Source as TTMSFNCFilterBuilderParseFormat).SmallerThanOperator;
//    FNotContainsOperator := (Source as TTMSFNCFilterBuilderParseFormat).NotContainsOperator;
    FNotEmptyOperator := (Source as TTMSFNCFilterBuilderParseFormat).NotEmptyOperator;
    FNotEqualOperator := (Source as TTMSFNCFilterBuilderParseFormat).NotEqualOperator;
//    FNotContainsOperator := (Source as TTMSFNCFilterBuilderParseFormat).NotContainsOperator;
    FOrOperator := (Source as TTMSFNCFilterBuilderParseFormat).OrOperator;
//    FXOrOperator := (Source as TTMSFNCFilterBuilderParseFormat).XOrOperator;
    FMultiCharWildCard := (Source as TTMSFNCFilterBuilderParseFormat).MultiCharWildCard;
    FSingleCharWildCard := (Source as TTMSFNCFilterBuilderParseFormat).SingleCharWildCard;
    FStartsWithOperator := (Source as TTMSFNCFilterBuilderParseFormat).StartsWithOperator;
    FStringDelimiter := (Source as TTMSFNCFilterBuilderParseFormat).StringDelimiter;
  end
  else
    inherited;
end;

procedure TTMSFNCFilterBuilderParseFormat.BeginUpdate;
begin
  Inc(FUpdateCount);
end;

constructor TTMSFNCFilterBuilderParseFormat.Create;
begin
  FUpdateCount := 0;
  FStringDelimiter := '''';
  FSingleCharWildCard := '_';
  FMultiCharWildCard := '%';

  FAndOperator := 'AND';
  FOrOperator := 'OR';
  FNotOperator := 'NOT';
//  FXOrOperator := 'XOR';

  FGroupFormat := '(#GROUP)';
  FColumnFormat := '[#COLUMN]';

  FContainsOperator := 'LIKE ' + FStringDelimiter + FMultiCharWildCard + '#VALUE' + FMultiCharWildCard + FStringDelimiter;
  FStartsWithOperator := 'LIKE ' + FStringDelimiter + '#VALUE' + FMultiCharWildCard + FStringDelimiter;
  FEndsWithOperator := 'LIKE ' + FStringDelimiter + FMultiCharWildCard + '#VALUE' + FStringDelimiter;
//  FNotContainsOperator := 'not ' + FColumnFormat + ' LIKE ' + FStringDelimiter + FMultiCharWildCard + '#VALUE' + FMultiCharWildCard + FStringDelimiter;
  FEmptyOperator := 'IS NULL';
  FNotEmptyOperator := 'IS NOT NULL';
  FEqualOperator := '= #VALUE';
  FEqualStrOperator := 'LIKE ' + FStringDelimiter + '#VALUE' + FStringDelimiter;
  FNotEqualOperator := '<> #VALUE';
  FLargerThanOrEqualOperator := '>= #VALUE';
  FLargerThanOperator := '> #VALUE';
  FSmallerThanOrEqualOperator := '<= #VALUE';
  FSmallerThanOperator := '< #VALUE';
end;

procedure TTMSFNCFilterBuilderParseFormat.DoInternalChanged;
begin
  if FUpdateCount > 0 then
  begin
    FModified := True;
    Exit;
  end;

  if Assigned(FOnInternalChanged) then
    FOnInternalChanged(Self);
end;

procedure TTMSFNCFilterBuilderParseFormat.EndUpdate;
begin
  Dec(FUpdateCount);
  if FUpdateCount <= 0 then
  begin
    FUpdateCount := 0;
    if FModified then
      DoInternalChanged;
    FModified := False;
  end;
end;

function TTMSFNCFilterBuilderParseFormat.IsAndOperatorStored: Boolean;
begin
  Result := FAndOperator <> 'AND';
end;

function TTMSFNCFilterBuilderParseFormat.IsColumnFormatStored: Boolean;
begin
  Result := FColumnFormat <> '[#COLUMN]';
end;

function TTMSFNCFilterBuilderParseFormat.IsContainsOperatorStored: Boolean;
begin
  Result := FContainsOperator <> 'LIKE ' + QuotedStr('%#VALUE%');
end;

function TTMSFNCFilterBuilderParseFormat.IsEmptyOperatorStored: Boolean;
begin
  Result := FEmptyOperator <> 'IS NULL';
end;

function TTMSFNCFilterBuilderParseFormat.IsEndsWithOperatorStored: Boolean;
begin
  Result := FEndsWithOperator <> 'LIKE ' + QuotedStr('%#VALUE');
end;

function TTMSFNCFilterBuilderParseFormat.IsEqualOperatorStored: Boolean;
begin
  Result := FEqualOperator <> '= #VALUE';
end;

function TTMSFNCFilterBuilderParseFormat.IsEqualStrOperatorStored: Boolean;
begin
  Result := FEqualStrOperator <> 'LIKE ' + QuotedStr('#VALUE')
end;

function TTMSFNCFilterBuilderParseFormat.IsLargerThanOrEqualOperatorStored: Boolean;
begin
  Result := FLargerThanOrEqualOperator <> '>= #VALUE';
end;

function TTMSFNCFilterBuilderParseFormat.IsLargerThanOperatorStored: Boolean;
begin
  Result := FLargerThanOperator <> '> #VALUE';
end;

function TTMSFNCFilterBuilderParseFormat.IsMultiCharWildCardStored: Boolean;
begin
  Result := FMultiCharWildCard <> '%';
end;

function TTMSFNCFilterBuilderParseFormat.IsGroupFormatStored: Boolean;
begin
  Result :=  FGroupFormat <> '(#GROUP)';
end;

function TTMSFNCFilterBuilderParseFormat.IsSingleCharWildCardStored: Boolean;
begin
  Result := FSingleCharWildCard <> '_';
end;

function TTMSFNCFilterBuilderParseFormat.IsSmallerThanOrEqualOperatorStored: Boolean;
begin
  Result := FSmallerThanOrEqualOperator <> '<= #VALUE';
end;

function TTMSFNCFilterBuilderParseFormat.IsSmallerThanOperatorStored: Boolean;
begin
  Result := FSmallerThanOperator <> '< #VALUE';
end;

//function TTMSFNCFilterBuilderParseFormat.IsNotContainsOperatorStored: Boolean;
//begin
//  Result := FNotContainsOperator <> '<> "*#VALUE*"';
//end;

function TTMSFNCFilterBuilderParseFormat.IsNotEmptyOperatorStored: Boolean;
begin
  Result := FNotEmptyOperator <> 'IS NOT NULL';
end;

function TTMSFNCFilterBuilderParseFormat.IsNotEqualOperatorStored: Boolean;
begin
  Result := FNotEqualOperator <> '<> #VALUE';
end;

function TTMSFNCFilterBuilderParseFormat.IsNotOperatorStored: Boolean;
begin
  Result := FNotOperator <> 'NOT';
end;

function TTMSFNCFilterBuilderParseFormat.IsOrOperatorStored: Boolean;
begin

  Result := FOrOperator <> 'OR';
end;

function TTMSFNCFilterBuilderParseFormat.IsStartsWithOperatorStored: Boolean;
begin
  Result := FStartsWithOperator <> 'LIKE ' + QuotedStr('#VALUE%');
end;

function TTMSFNCFilterBuilderParseFormat.IsStringDelimiterStored: Boolean;
begin
  Result := FStringDelimiter <> '''';
end;

//function TTMSFNCFilterBuilderParseFormat.IsXOrOperatorStored: Boolean;
//begin
//  Result := FXOrOperator <> 'XOR';
//end;

procedure TTMSFNCFilterBuilderParseFormat.SetAndOperator(const Value: string);
begin
  FAndOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetColumnFormat(const Value: string);
begin
  FColumnFormat := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetContainsOperator(const Value: string);
begin
  FContainsOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetEmptyOperator(const Value: string);
begin
  FEmptyOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetEndsWithOperator(const Value: string);
begin
  FEndsWithOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetEqualOperator(const Value: string);
begin
  FEqualOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetEqualStrOperator(const Value: string);
begin
  FEqualStrOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetGroupFormat(const Value: string);
begin
  FGroupFormat := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetLargerThanOperator(const Value: string);
begin
  FLargerThanOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetLargerThanOrEqualOperator(const Value: string);
begin
  FLargerThanOrEqualOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetMultiCharWildCard(const Value: string);
begin
  FMultiCharWildCard := Value;
  DoInternalChanged;
end;

//procedure TTMSFNCFilterBuilderParseFormat.SetNotContainsOperator(const Value: string);
//begin
//  FNotContainsOperator := Value;
//  DoInternalChanged;
//end;

procedure TTMSFNCFilterBuilderParseFormat.SetNotEmptyOperator(const Value: string);
begin
  FNotEmptyOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetNotEqualOperator(const Value: string);
begin
  FNotEqualOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetNotOperator(const Value: string);
begin
  FNotOperator := Value;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetOrOperator(const Value: string);
begin
  FOrOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetSingleCharWildCard(const Value: string);
begin
  FSingleCharWildCard := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetSmallerThanOperator(const Value: string);
begin
  FSmallerThanOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetSmallerThanOrEqualOperator(const Value: string);
begin
  FSmallerThanOrEqualOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetStartsWithOperator(const Value: string);
begin
  FStartsWithOperator := Value;
  DoInternalChanged;
end;

procedure TTMSFNCFilterBuilderParseFormat.SetStringDelimiter(const Value: string);
begin
  FStringDelimiter := Value;
  DoInternalChanged;
end;

//procedure TTMSFNCFilterBuilderParseFormat.SetXOrOperator(const Value: string);
//begin
//  FXOrOperator := Value;
//  DoInternalChanged;
//end;

//function MatchStrings(aPattern: string; AExpression: string; AMultiCharWildCard: string = ''; ASingleCharWildCard: string = ''): Boolean;
//var
//  PIndex, EIndex: Integer;
//  PLen, ELen: Integer;
//  BacktrackStack: TArray<TPair<Integer, Integer>>;
//begin
//  Result := False;
//  PIndex := 1; // 1-based index for Pattern
//  EIndex := 1; // 1-based index for Expression
//  PLen := Length(APattern);
//  ELen := Length(AExpression);
//  while (PIndex <= PLen) or (EIndex <= ELen) do
//  begin
//    if (AMultiCharWildCard <> '') and (PIndex <= PLen) and (APattern[PIndex] = AMultiCharWildCard) then
//    begin
//      // Multi-character wildcard
//      SetLength(BacktrackStack, Length(BacktrackStack) + 1);
//      BacktrackStack[High(BacktrackStack)] := TPair<Integer, Integer>.Create(PIndex, EIndex + 1);
//      Inc(PIndex);
//    end
//    else if (ASingleCharWildCard <> '') and (PIndex <= PLen) and (EIndex <= ELen) and ((APattern[PIndex] = ASingleCharWildCard) or (APattern[PIndex] = AExpression[EIndex])) then
//    begin
//      // Single-character match or wildcard match
//      Inc(PIndex);
//      Inc(EIndex);
//    end
//    else if Length(BacktrackStack) > 0 then
//    begin
//      // Backtracking if previous multi-wildcard failed
//      PIndex := BacktrackStack[High(BacktrackStack)].Key;
//      EIndex := BacktrackStack[High(BacktrackStack)].Value;
//      SetLength(BacktrackStack, Length(BacktrackStack) - 1);
//    end
//    else
//    begin
//      Exit; // No match found
//    end;
//  end;
//  // Ensure both the pattern and the expression are fully consumed
//  Result := (PIndex > PLen) and (EIndex > ELen);
//end;

function MatchStrings(aPattern: string; AExpression: string; AMultiCharWildCard: string = ''; ASingleCharWildCard: string = ''): Boolean;
var
  PIndex, EIndex: Integer;
  PLen, ELen: Integer;
  LastStarP, LastStarE: Integer;
begin
//  Result := False;
  PIndex := 1; // Pattern index
  EIndex := 1; // Expression index
  PLen := Length(aPattern);
  ELen := Length(AExpression);
  LastStarP := 0; // Last position of '*' (or '%') in pattern
  LastStarE := 0; // Corresponding position in expression

  while (EIndex <= ELen) do
  begin
    if (PIndex <= PLen) and
       ((aPattern[PIndex] = AExpression[EIndex]) or
        ((ASingleCharWildCard <> '') and (aPattern[PIndex] = ASingleCharWildCard))) then
    begin
      // Direct match or single-character wildcard
      Inc(PIndex);
      Inc(EIndex);
    end
    else if (PIndex <= PLen) and (AMultiCharWildCard <> '') and (aPattern[PIndex] = AMultiCharWildCard) then
    begin
      // Multi-character wildcard match
      LastStarP := PIndex;
      LastStarE := EIndex;
      Inc(PIndex);
    end
    else if (LastStarP > 0) then
    begin
      // Backtracking: try to extend the multi-character wildcard match
      PIndex := LastStarP + 1;
      Inc(LastStarE);
      EIndex := LastStarE;
    end
    else
    begin
      Exit(False); // No match found, and no wildcard to backtrack
    end;
  end;

  // Skip remaining '*' or '%' wildcards in the pattern
  while (PIndex <= PLen) and (aPattern[PIndex] = AMultiCharWildCard) do
    Inc(PIndex);

  Result := PIndex > PLen;
end;

{ TTMSFNCFilterBuilderCustomUILanguage }

procedure TTMSFNCFilterBuilderCustomUILanguage.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCFilterBuilderCustomUILanguage then
  begin
    FClose := (Source as TTMSFNCFilterBuilderCustomUILanguage).Close;
    FApply := (Source as TTMSFNCFilterBuilderCustomUILanguage).Apply;
    FOK := (Source as TTMSFNCFilterBuilderCustomUILanguage).OK;
    FCaption := (Source as TTMSFNCFilterBuilderCustomUILanguage).Caption;
    FAndOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).AndOperatorText;
    FOrOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).OrOperatorText;
    FEmptyOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).EmptyOperatorText;
    FEqualOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).EqualOperatorText;
    FLargerThanOrEqualOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).LargerThanOrEqualOperatorText;
    FLargerThanOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).LargerThanOperatorText;
    FSmallerThanOrEqualOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).SmallerThanOrEqualOperatorText;
    FSmallerThanOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).SmallerThanOperatorText;
    FNotEmptyOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).NotEmptyOperatorText;
    FNotEqualOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).NotEqualOperatorText;
    FStartsWithOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).StartsWithOperatorText;
    FEndsWithOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).EndsWithOperatorText;
    FContainsOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).ContainsOperatorText;
    FNotContainsOperatorText := (Source as TTMSFNCFilterBuilderCustomUILanguage).NotContainsOperatorText;
    FTrueText := (Source as TTMSFNCFilterBuilderCustomUILanguage).TrueText;
    FFalseText := (Source as TTMSFNCFilterBuilderCustomUILanguage).FalseText;
    FAddHint := (Source as TTMSFNCFilterBuilderCustomUILanguage).AddHint;
    FRemoveHint := (Source as TTMSFNCFilterBuilderCustomUILanguage).RemoveHint;
    FNotHint := (Source as TTMSFNCFilterBuilderCustomUILanguage).NotHint;
    FColumnNameHint := (Source as TTMSFNCFilterBuilderCustomUILanguage).ColumnNameHint;
    FOperatorHint := (Source as TTMSFNCFilterBuilderCustomUILanguage).OperatorHint;
    FValueHint := (Source as TTMSFNCFilterBuilderCustomUILanguage).ValueHint;
  end
  else
    inherited;
end;

procedure TTMSFNCFilterBuilderCustomUILanguage.DoChanged;
begin
  if Assigned(FOnInternalChanged) then
    FOnInternalChanged(Self);
end;

constructor TTMSFNCFilterBuilderCustomUILanguage.Create;
begin
  FApply := sTMSFNCFilterBuilderUILanguageApply;
  FOk := sTMSFNCFilterBuilderUILanguageOk;
  FClear := sTMSFNCFilterBuilderUILanguageClear;
  FCancel := sTMSFNCFilterBuilderUILanguageCancel;
  FClose := sTMSFNCFilterBuilderUILanguageClose;
  FCaption := sTMSFNCFilterBuilderUILanguageCaption;
  FAndOperatorText := sTMSFNCFilterBuilderUILanguageAndOperatorText;
  FOrOperatorText := sTMSFNCFilterBuilderUILanguageOrOperatorText;
  FEmptyOperatorText := sTMSFNCFilterBuilderUILanguageEmptyOperatorText;
  FEqualOperatorText := sTMSFNCFilterBuilderUILanguageEqualOperatorText;
  FLargerThanOrEqualOperatorText := sTMSFNCFilterBuilderUILanguageLargerThanOrEqualOperatorText;
  FLargerThanOperatorText := sTMSFNCFilterBuilderUILanguageLargerThanOperatorText;
  FSmallerThanOrEqualOperatorText := sTMSFNCFilterBuilderUILanguageSmallerThanOrEqualOperatorText;
  FSmallerThanOperatorText := sTMSFNCFilterBuilderUILanguageSmallerThanOperatorText;
  FNotEmptyOperatorText := sTMSFNCFilterBuilderUILanguageNotEmptyOperatorText;
  FNotEqualOperatorText := sTMSFNCFilterBuilderUILanguageNotEqualOperatorText;
  FStartsWithOperatorText := sTMSFNCFilterBuilderUILanguageStartsWithOperatorText;
  FEndsWithOperatorText := sTMSFNCFilterBuilderUILanguageEndsWithOperatorText;
  FContainsOperatorText := sTMSFNCFilterBuilderUILanguageContainsOperatorText;
  FNotContainsOperatorText := sTMSFNCFilterBuilderUILanguageNotContainsOperatorText;
  FAddExpression := sTMSFNCFilterBuilderUILanguageAddExpression;
  FAddGroup := sTMSFNCFilterBuilderUILanguageAddGroup;
  FTrueText := sTMSFNCFilterBuilderUILanguageTrue;
  FFalseText := sTMSFNCFilterBuilderUILanguageFalse;
  FAddHint := sTMSFNCFilterBuilderUILanguageAddHint;
  FRemoveHint := sTMSFNCFilterBuilderUILanguageRemoveHint;
  FNotHint := sTMSFNCFilterBuilderUILanguageNotHint;
  FColumnNameHint := sTMSFNCFilterBuilderUILanguageColumnNameHint;
  FOperatorHint := sTMSFNCFilterBuilderUILanguageOperatorHint;
  FValueHint := sTMSFNCFilterBuilderUILanguageValueHint;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsAddExpressionStored: Boolean;
begin
  Result := FAddExpression <> sTMSFNCFilterBuilderUILanguageAddExpression;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsAddGroupStored: Boolean;
begin
  Result := FAddGroup <> sTMSFNCFilterBuilderUILanguageAddGroup;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsAddHintStored: Boolean;
begin
  Result := FAddHint <> sTMSFNCFilterBuilderUILanguageAddHint;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsAndOperatorTextStored: Boolean;
begin
  Result := FAndOperatorText <> sTMSFNCFilterBuilderUILanguageAndOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsApplyStored: Boolean;
begin
  Result := FApply <> sTMSFNCFilterBuilderUILanguageApply;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsOKStored: Boolean;
begin
  Result := FOk <> sTMSFNCFilterBuilderUILanguageOk;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsOperatorHintStored: Boolean;
begin
  Result := FOperatorHint <> sTMSFNCFilterBuilderUILanguageOperatorHint;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsOrOperatorTextStored: Boolean;
begin
  Result := FOrOperatorText <> sTMSFNCFilterBuilderUILanguageOrOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsRemoveHintStored: Boolean;
begin
  Result := FRemoveHint <> sTMSFNCFilterBuilderUILanguageRemoveHint;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsStartsWithOperatorTextStored: Boolean;
begin
  Result := FStartsWithOperatorText <> sTMSFNCFilterBuilderUILanguageStartsWithOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsValueHintStored: Boolean;
begin
  Result := FValueHint <> sTMSFNCFilterBuilderUILanguageValueHint;
end;

function TTMSFNCFilterBuilderCustomUILanguage.TrueOperatorTextStored: Boolean;
begin
  Result := FTrueText <> sTMSFNCFilterBuilderUILanguageTrue;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsCancelStored: Boolean;
begin
  Result := FCancel <> sTMSFNCFilterBuilderUILanguageCancel;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsCaptionStored: Boolean;
begin
  Result := FCaption <> sTMSFNCFilterBuilderUILanguageCaption;
end;

//function TTMSFNCFilterBuilderCustomUILanguage.IsCaseHintStored: Boolean;
//begin
//  Result := FCaseHint <> sTMSFNCFilterBuilderUILanguageCaseHint);
//end;

function TTMSFNCFilterBuilderCustomUILanguage.IsClearStored: Boolean;
begin
  Result := FClear <> sTMSFNCFilterBuilderUILanguageClear;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsCloseStored: Boolean;
begin
  Result := FClose <> sTMSFNCFilterBuilderUILanguageClose;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsColumnNameHintStored: Boolean;
begin
  Result := FColumnNameHint <> sTMSFNCFilterBuilderUILanguageColumnNameHint;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsContainsOperatorTextStored: Boolean;
begin
  Result := FContainsOperatorText <> sTMSFNCFilterBuilderUILanguageContainsOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsEmptyOperatorTextStored: Boolean;
begin
  Result := FEmptyOperatorText <> sTMSFNCFilterBuilderUILanguageEmptyOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsEndsWithOperatorTextStored: Boolean;
begin
  Result := FEndsWithOperatorText <> sTMSFNCFilterBuilderUILanguageEndsWithOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsEqualOperatorTextStored: Boolean;
begin
  Result := FEqualOperatorText <> sTMSFNCFilterBuilderUILanguageEqualOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.FalseOperatorTextStored: Boolean;
begin
  Result := FFalseText <> sTMSFNCFilterBuilderUILanguageFalse;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsLargerThanOrEqualOperatorTextStored: Boolean;
begin
  Result := FLargerThanOrEqualOperatorText <> sTMSFNCFilterBuilderUILanguageLargerThanOrEqualOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsLargerThanOperatorTextStored: Boolean;
begin
  Result := FLargerThanOperatorText <> sTMSFNCFilterBuilderUILanguageLargerThanOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsSmallerThanOrEqualOperatorTextStored: Boolean;
begin
  Result := FSmallerThanOrEqualOperatorText <> sTMSFNCFilterBuilderUILanguageSmallerThanOrEqualOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsSmallerThanOperatorTextStored: Boolean;
begin
  Result := FSmallerThanOperatorText <> sTMSFNCFilterBuilderUILanguageSmallerThanOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsNotContainsOperatorTextStored: Boolean;
begin
  Result := FNotContainsOperatorText <> sTMSFNCFilterBuilderUILanguageNotContainsOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsNotEmptyOperatorTextStored: Boolean;
begin
  Result := FNotEmptyOperatorText <> sTMSFNCFilterBuilderUILanguageNotEmptyOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsNotEqualOperatorTextStored: Boolean;
begin
  Result := FNotEqualOperatorText <> sTMSFNCFilterBuilderUILanguageNotEqualOperatorText;
end;

function TTMSFNCFilterBuilderCustomUILanguage.IsNotHintStored: Boolean;
begin
  Result := FNotHint <> sTMSFNCFilterBuilderUILanguageNotHint;
end;

initialization
begin
  RegisterClasses([TTMSFNCFilterBuilderGroup, TTMSFNCFilterBuilderExpression, TTMSFNCFilterBuilderColumn]);
end;

{$IFNDEF WEBLIB}
finalization
begin
  if Assigned(FTMSFNCFilterBuilder) then
  begin
    FTMSFNCFilterBuilder.Free;
    FTMSFNCFilterBuilder := nil;
  end;
end;
{$ENDIF}

end.
